/* Invoices (NF-e / NFC-e / Cupom / Avulsa) — list + create + duplicate.
 *
 * Phase 4 SEFAZ: persists as `draft`. The actual SEFAZ signing / SOAP
 * transmission lives in a future phase (pynfe). The user only picks
 * a Natureza de Operacao + items + client; CFOP/CST/aliquotas are
 * abstracted away.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';
import { confirmDialog } from '../components/ConfirmDialog';
import {
  Plus, X, Save, Copy, Trash2, FileText, Search, Receipt, CheckCircle, XCircle, Clock, Send, Download, Printer, Ban, FileEdit, AlertCircle, Edit, Settings, AlertTriangle, ChevronDown, Package, Truck, RefreshCw, Check, ShoppingCart, Calendar,
} from 'lucide-react';

const ERP = `${API}/erp`;
const inputClass = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-violet-600 focus:ring-2 focus:ring-violet-500/20";
const labelCls = "block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1";

const TIPO_LABEL = {
  nfe: { label: 'NF-e', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  nfce: { label: 'NFC-e', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  cupom: { label: 'Cupom', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  avulsa: { label: 'Avulsa', color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const STATUS_LABEL = {
  draft: { label: 'Rascunho', icon: Clock, color: 'bg-slate-100 text-slate-700' },
  issued: { label: 'Autorizada', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelada', icon: XCircle, color: 'bg-rose-100 text-rose-700' },
  rejected: { label: 'Rejeitada', icon: XCircle, color: 'bg-rose-100 text-rose-700' },
  pending: { label: 'Pendente SEFAZ', icon: Clock, color: 'bg-amber-100 text-amber-700' },
};

// iter156ad: separador de milhar (1.234,56) — antes so pontuava centavos (1234,56).
const fmtBRL = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR') + ' ' + new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 10); }
};

export const InvoicesPage = ({ companyId }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  // iter156fl: filtro de periodo — padrao mes atual (evita carregar todas as notas de uma vez)
  const _fmtD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const _monthRange = (offset) => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const last = offset === 0 ? now : new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
    return { from: _fmtD(first), to: _fmtD(last) };
  };
  const [periodChip, setPeriodChip] = useState('month');
  const [dateFrom, setDateFrom] = useState(() => _monthRange(0).from);
  const [dateTo, setDateTo] = useState(() => _monthRange(0).to);
  const pickPeriod = (chip) => {
    setPeriodChip(chip);
    if (chip === 'prev_month') { const r = _monthRange(-1); setDateFrom(r.from); setDateTo(r.to); }
    else if (chip === 'month') { const r = _monthRange(0); setDateFrom(r.from); setDateTo(r.to); }
  };
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInutModal, setShowInutModal] = useState(false);
  const [cceModal, setCceModal] = useState(null);   // { invoice }
  // iter77aj: Diagnostico de rejeicao SEFAZ + config check
  const [diagnosisModal, setDiagnosisModal] = useState(null);   // { invoice }
  const [configCheck, setConfigCheck] = useState({ loading: true, missing: false, reasons: [] });

  // iter77aj: verifica se ha certificado e config minima ANTES de permitir emissao
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!companyId) { setConfigCheck({ loading: false, missing: false, reasons: [] }); return; }
      const reasons = [];
      try {
        // iter156ed: cert e por FILIAL — sem company_id o backend devolvia qualquer cert do tenant
        // (podia ser um vencido de outra filial) e mostrava "Certificado VENCIDO" indevidamente.
        const { data: cert } = await axios.get(`${ERP}/fiscal-cert?company_id=${companyId}`, { withCredentials: true });
        if (!cert?.data) reasons.push({ label: 'Certificado Digital A1 (.pfx)', path: '/erp/fiscal-config' });
        else if (cert.data.expired) reasons.push({ label: 'Certificado VENCIDO - reupload necessario', path: '/erp/fiscal-config' });
      } catch { reasons.push({ label: 'Certificado Digital A1 (.pfx)', path: '/erp/fiscal-config' }); }
      try {
        const { data: natOps } = await axios.get(`${ERP}/natureza-operacao?company_id=${companyId}`, { withCredentials: true });
        if (!(natOps?.data || []).length) reasons.push({ label: 'Naturezas de Operacao', path: '/erp/fiscal-config' });
      } catch { /* tolerante */ }
      if (alive) setConfigCheck({ loading: false, missing: reasons.length > 0, reasons });
    })();
    return () => { alive = false; };
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId) { setInvoices([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ company_id: companyId });
      if (filterTipo) params.set('tipo', filterTipo);
      if (filterStatus) params.set('status', filterStatus);
      if (search) params.set('q', search);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data } = await axios.get(`${ERP}/invoices?${params.toString()}`, { withCredentials: true });
      setInvoices(data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar notas');
    } finally { setLoading(false); }
  }, [companyId, filterTipo, filterStatus, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // iter77cz: auto-abre formulario de edicao quando a URL tem ?highlight=<invoice_id>&tipo=<tipo>.
  // Usado pelo fluxo Vendas → "Gerar NF-e/NFC-e" quando faltam dados no rascunho.
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    const highlightId = params.get('highlight');
    const highlightTipo = params.get('tipo');
    if (!highlightId) return;
    const inv = invoices.find(i => i.id === highlightId);
    if (inv) {
      setEditing(inv);
      setShowForm(true);
    } else if (highlightTipo) {
      // Nao encontrado na lista atual (talvez outro filtro) — busca direta pelo id
      (async () => {
        try {
          const { data } = await axios.get(`${ERP}/invoices/${highlightId}`, { withCredentials: true });
          if (data && data.id) {
            setEditing(data);
            setShowForm(true);
          }
        } catch { /* silent */ }
      })();
    }
    // limpa a query string para nao reabrir ao voltar/refresh
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('highlight');
      url.searchParams.delete('tipo');
      window.history.replaceState(null, '', url.pathname + (url.search || ''));
    } catch { /* silent */ }
  }, [loading, invoices]);

  const handleDuplicate = async (inv) => {
    try {
      await axios.post(`${ERP}/invoices/${inv.id}/duplicate`, {}, { withCredentials: true });
      toast.success('Nota duplicada como novo rascunho');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao duplicar');
    }
  };

  const handleDelete = async (inv) => {
    const ok = await confirmDialog({
      title: `Excluir nota ${TIPO_LABEL[inv.tipo]?.label || inv.tipo} #${inv.numero}?`,
      message: 'A nota sera removida do sistema.',
      description: 'Em ambiente de PRODUCAO, apenas rascunhos podem ser excluidos. Em HOMOLOGACAO, todas as notas podem ser removidas.',
      confirmLabel: 'Excluir',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${ERP}/invoices/${inv.id}`, { withCredentials: true });
      toast.success('Nota excluida');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleCancel = async (inv) => {
    const ok = await confirmDialog({
      title: `Cancelar nota ${TIPO_LABEL[inv.tipo]?.label || inv.tipo} #${inv.numero}?`,
      message: 'A nota sera cancelada no SEFAZ (evento de cancelamento).',
      description: 'Esta acao e irreversivel. O cancelamento e permitido em ate 24h apos a autorizacao.',
      confirmLabel: 'Cancelar nota',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.post(`${ERP}/invoices/${inv.id}/cancel`, {}, { withCredentials: true });
      toast.success('Nota cancelada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const handleIssue = async (inv) => {
    if (!(inv.tipo === 'nfe' || inv.tipo === 'nfce')) { toast.error('So NF-e e NFC-e podem ir ao SEFAZ'); return; }
    if (inv.max_attempts_reached) {
      toast.error(`NF #${inv.numero} ja atingiu o limite de 5 tentativas. INUTILIZE o numero e emita uma nova nota.`, { duration: 6000 });
      return;
    }
    // iter77bh: transmite direto ao SEFAZ — sem confirm intermediario (request do usuario).
    const tid = toast.loading('Assinando e enviando ao SEFAZ...');
    try {
      const { data } = await axios.post(`${ERP}/invoices/${inv.id}/issue`, {}, { withCredentials: true, timeout: 95000 });
      toast.dismiss(tid);
      if (data.status === 'issued') {
        toast.success(`Autorizada! Protocolo ${data.protocolo || '—'}`);
      } else {
        // iter77dk: toast agora mostra o motivo SEFAZ direto (cStat + motivo) quando disponivel.
        // Antes so pedia pra clicar em "Diagnostico" sem dizer o que era.
        const cstat = data.sefaz_cstat || data.cstat;
        const motivo = data.sefaz_motivo || data.motivo || data.sefaz_message;
        const detail = (cstat || motivo) ? `cStat ${cstat || '?'}: ${motivo || '(sem motivo detalhado)'}` : (data.sefaz_error || 'Sem detalhes disponiveis');
        toast.error(
          `NF #${inv.numero} rejeitada — ${detail}\nClique em "Diagnostico" na linha pra ver completo.`,
          { duration: 12000 }
        );
      }
      load();
    } catch (err) {
      toast.dismiss(tid);
      // iter156ag: detectar Cloudflare 5xx (nao veio JSON — veio HTML) e mostrar mensagem clara.
      // Status 520/521/522/523/524: erros de conectividade Cloudflare -> backend
      const status = err.response?.status;
      const isCloudflareErr = [520, 521, 522, 523, 524].includes(status);
      const isHtml = typeof err.response?.data === 'string' && err.response.data.includes('<html');
      if (isCloudflareErr || isHtml || err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error(
          `Conexao com SEFAZ demorou mais que o esperado (${status || 'timeout'}). A nota pode ter sido autorizada. Clique em "Consultar Status SEFAZ" na linha da nota em ~30 segundos para confirmar. NAO reenvie ainda para evitar duplicidade.`,
          { duration: 15000 }
        );
        load();
        return;
      }
      // iter77an: server-side preflight returns 422 with structured 'problems' list — show them all.
      const detail = err.response?.data?.detail;
      // iter77ax: trata MAX_ATTEMPTS_REACHED de forma especifica
      if (detail && typeof detail === 'object' && detail.code === 'MAX_ATTEMPTS_REACHED') {
        toast.error(detail.message || 'Limite de tentativas atingido', { duration: 10000 });
        load();
        return;
      }
      if (detail && typeof detail === 'object' && Array.isArray(detail.problems)) {
        toast.error(
          (t) => (
            <div className="space-y-1.5" data-testid="issue-preflight-toast">
              <p className="font-bold text-rose-700">{detail.message || 'Nota com problemas'}</p>
              <ul className="text-xs space-y-0.5 list-disc list-inside">
                {detail.problems.slice(0, 8).map((p, i) => <li key={i}>{p}</li>)}
              </ul>
              {detail.problems.length > 8 && <p className="text-[10px] text-slate-500">+{detail.problems.length - 8} mais...</p>}
              <button onClick={() => toast.dismiss(t.id)} className="mt-1 px-2 py-1 bg-rose-600 text-white text-[10px] font-bold rounded">OK</button>
            </div>
          ),
          { duration: 15000 }
        );
      } else {
        // iter77dk: melhoria pra mostrar detalhe real em vez de "Erro ao emitir SEFAZ" generico.
        // detail pode vir como:
        //   - string: mostra direto
        //   - object com {message, sefaz_message, sefaz_cstat, motivo, error, ...}: extrai
        //   - undefined/null: usa mensagem HTTP ou generica
        let msg = 'Erro ao emitir SEFAZ';
        if (typeof detail === 'string') {
          msg = detail;
        } else if (detail && typeof detail === 'object') {
          // Prioridades: message > motivo (SEFAZ) > sefaz_message > error > JSON stringify
          msg = detail.message
             || (detail.sefaz_cstat && detail.sefaz_motivo ? `SEFAZ cStat ${detail.sefaz_cstat}: ${detail.sefaz_motivo}` : null)
             || detail.sefaz_message
             || detail.motivo
             || detail.error
             || detail.detail
             || JSON.stringify(detail);
        } else if (err.message) {
          msg = err.message;
        }
        toast.error(msg, { duration: 12000 });
        console.error('[emit-sefaz] full error response:', err.response?.data || err);
      }
    }
  };

  // iter77ay: Consulta SEFAZ status (nota pendente OU forcar reconciliacao)
  const handleConsultStatus = async (inv) => {
    const tid = toast.loading('Consultando status no SEFAZ...');
    try {
      const { data } = await axios.post(`${ERP}/invoices/${inv.id}/consult-status`, {}, { withCredentials: true, timeout: 30000 });
      toast.dismiss(tid);
      if (data.status === 'issued') {
        toast.success(`Nota autorizada! Protocolo ${data.protocolo || '—'}`);
      } else if (data.status === 'pending') {
        toast(`SEFAZ ainda processando (cStat ${data.sefaz_cstat}). Tente novamente em alguns minutos.`, { icon: '⏳' });
      } else if (data.status === 'rejected') {
        toast.error(`Nota rejeitada (cStat ${data.sefaz_cstat}). Veja o Diagnostico.`);
      } else {
        toast(`Status: ${data.status} (cStat ${data.sefaz_cstat})`);
      }
      load();
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'Erro ao consultar SEFAZ');
    }
  };

  const handleViewDanfe = async (inv) => {
    try {
      const { data: blob } = await axios.get(`${ERP}/invoices/${inv.id}/danfe`, { responseType: 'blob', withCredentials: true });
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        const a = document.createElement('a'); a.href = url; a.download = `danfe-${inv.numero}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      // Try to read blob error
      let msg = 'Erro ao gerar DANFE';
      try {
        const text = await err.response?.data?.text?.();
        if (text) { try { msg = JSON.parse(text).detail || msg; } catch { msg = text.slice(0, 200); } }
      } catch { /* ignore */ }
      toast.error(msg);
    }
  };

  const handleDownloadXml = async (inv) => {
    try {
      const { data } = await axios.get(`${ERP}/invoices/${inv.id}/xml`, { responseType: 'blob', withCredentials: true });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a'); a.href = url; a.download = `nfe-${inv.numero}.xml`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      let msg = 'Erro ao baixar XML';
      try { msg = JSON.parse(await err.response?.data?.text())?.detail || msg; } catch { /* ignore */ }
      toast.error(msg, { duration: 8000 });
    }
  };

  const totalsByStatus = useMemo(() => {
    const acc = { draft: 0, issued: 0, cancelled: 0 };
    invoices.forEach(i => { if (acc[i.status] != null) acc[i.status] += Number(i.total || 0); });
    return acc;
  }, [invoices]);

  if (!companyId) {
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 p-6 rounded-2xl text-center" data-testid="invoices-no-company">
        Selecione uma empresa para gerenciar Notas Fiscais.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="invoices-page">
      {/* iter77bd: Header compacto — sem titulo grande (titulo agora vive nas tabs) */}

      {/* iter77bc+bd: Tabs NF-e | NFC-e | Todas — agora cada tab tem seus proprios botoes contextuais */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="invoice-type-tabs">
        <div className="flex border-b border-slate-200 rounded-t-2xl overflow-hidden">
          {[
            { v: 'nfe', label: 'NF-e', sub: 'Modelo 55', icon: FileText, activeCls: 'border-violet-600 bg-violet-50', textCls: 'text-violet-700', iconCls: 'text-violet-600', badgeCls: 'bg-violet-200 text-violet-800' },
            { v: 'nfce', label: 'NFC-e', sub: 'Cupom Fiscal Modelo 65', icon: Receipt, activeCls: 'border-fuchsia-600 bg-fuchsia-50', textCls: 'text-fuchsia-700', iconCls: 'text-fuchsia-600', badgeCls: 'bg-fuchsia-200 text-fuchsia-800' },
            { v: '', label: 'Todas', sub: 'Inclui Cupom + Avulsa', icon: FileEdit, activeCls: 'border-slate-600 bg-slate-100', textCls: 'text-slate-700', iconCls: 'text-slate-600', badgeCls: 'bg-slate-300 text-slate-800' },
          ].map(t => {
            const TIcon = t.icon;
            const isActive = filterTipo === t.v;
            const count = invoices.filter(inv => t.v === '' ? true : inv.tipo === t.v).length;
            return (
              <button
                key={t.v || 'all'}
                type="button"
                onClick={() => setFilterTipo(t.v)}
                className={`flex-1 px-4 py-3 text-left transition-all border-b-2 ${isActive ? t.activeCls : 'border-transparent hover:bg-slate-50'}`}
                data-testid={`invoice-tab-${t.v || 'all'}`}
              >
                <div className="flex items-center gap-2">
                  <TIcon size={16} className={isActive ? t.iconCls : 'text-slate-400'} />
                  <span className={`font-black text-sm ${isActive ? t.textCls : 'text-slate-600'}`}>{t.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? t.badgeCls : 'bg-slate-100 text-slate-500'}`}>{count}</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">{t.sub}</p>
              </button>
            );
          })}
        </div>

        {/* iter77bd: Action bar contextual — dentro da aba selecionada */}
        <div className="bg-gradient-to-r from-slate-50 to-white px-4 py-3 flex items-center justify-between gap-2 border-b border-slate-200" data-testid="tab-action-bar">
          <p className="text-[11px] text-slate-500 italic">
            {filterTipo === 'nfe' && 'Emissao de NF-e (modelo 55) — vendas para CNPJ ou CPF identificado.'}
            {filterTipo === 'nfce' && 'Emissao de NFC-e (modelo 65) — cupom fiscal eletronico de varejo.'}
            {filterTipo === '' && 'Visualizacao geral. Para emitir, selecione uma aba especifica (NF-e ou NFC-e).'}
          </p>
          <div className="flex items-center gap-2">
            <TabMoreActionsButton
              filterTipo={filterTipo}
              onInutilizar={() => setShowInutModal(true)}
              companyId={companyId}
            />
            {filterTipo === 'nfe' && (
              <button
                onClick={() => { setEditing({ tipo: 'nfe' }); setShowForm(true); }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs shadow shadow-emerald-500/20 flex items-center gap-1.5"
                data-testid="invoice-new-nfe-btn"
                title="Emitir nova NF-e (modelo 55)"
              >
                <Plus size={14} /> Emitir NF-e
              </button>
            )}
            {filterTipo === 'nfce' && (
              <button
                onClick={() => { setEditing({ tipo: 'nfce' }); setShowForm(true); }}
                className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-bold text-xs shadow shadow-sky-500/20 flex items-center gap-1.5"
                data-testid="invoice-new-nfce-btn"
                title="Emitir nova NFC-e (modelo 65)"
              >
                <Plus size={14} /> Emitir NFC-e
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3 shadow-sm" data-testid="invoices-filters">
        <div className="relative md:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por cliente, documento, natureza..."
            className={`${inputClass} pl-9`}
            data-testid="invoice-search-input"
          />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={inputClass} data-testid="invoice-filter-status">
          <option value="">Todos os status</option>
          <option value="draft">Rascunho</option>
          <option value="issued">Autorizada</option>
          <option value="pending">Pendente SEFAZ</option>
          <option value="cancelled">Cancelada</option>
          <option value="rejected">Rejeitada</option>
        </select>
        <div className="md:col-span-3 flex flex-wrap items-center gap-2 pt-1" data-testid="invoice-period-filters">
          {[['prev_month', 'Mes Anterior'], ['month', 'Mes Atual'], ['custom', 'Personalizado']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => pickPeriod(k)} className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors ${periodChip === k ? 'bg-violet-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`invoice-period-${k}`}>{l}</button>
          ))}
          {periodChip === 'custom' && (
            <div className="flex items-center gap-2 ml-1">
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs text-slate-900 bg-white" data-testid="invoice-date-from" />
              <span className="text-xs text-slate-400">ate</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs text-slate-900 bg-white" data-testid="invoice-date-to" />
            </div>
          )}
          <span className="text-[11px] text-slate-400 ml-auto" data-testid="invoice-period-label">
            {dateFrom && dateTo ? `${dateFrom.split('-').reverse().join('/')} a ${dateTo.split('-').reverse().join('/')}` : 'Todo o periodo'}
          </span>
        </div>
      </div>

      {/* Quick KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="kpi-draft">
          <p className="text-[10px] font-bold text-slate-500 uppercase">Rascunhos</p>
          <p className="text-lg font-black text-slate-700">{fmtBRL(totalsByStatus.draft)}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="kpi-issued">
          <p className="text-[10px] font-bold text-emerald-600 uppercase">Autorizadas</p>
          <p className="text-lg font-black text-emerald-700">{fmtBRL(totalsByStatus.issued)}</p>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="kpi-cancelled">
          <p className="text-[10px] font-bold text-rose-600 uppercase">Canceladas</p>
          <p className="text-lg font-black text-rose-700">{fmtBRL(totalsByStatus.cancelled)}</p>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-violet-600 border-t-transparent" /></div>
        ) : invoices.length === 0 ? (
          <div className="p-10 text-center" data-testid="invoices-empty">
            <FileText size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">Nenhuma nota encontrada.</p>
            <p className="text-xs text-slate-400 mt-1">Clique em &quot;Nova Nota&quot; para emitir a primeira.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2.5 font-bold text-xs uppercase">Numero / Tipo</th>
                  <th className="text-left px-3 py-2.5 font-bold text-xs uppercase">Cliente</th>
                  <th className="text-left px-3 py-2.5 font-bold text-xs uppercase">Natureza</th>
                  <th className="text-left px-3 py-2.5 font-bold text-xs uppercase">Data</th>
                  <th className="text-right px-3 py-2.5 font-bold text-xs uppercase">Total</th>
                  <th className="text-center px-3 py-2.5 font-bold text-xs uppercase">Status</th>
                  <th className="text-right px-3 py-2.5 font-bold text-xs uppercase">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map(inv => {
                  const tipoMeta = TIPO_LABEL[inv.tipo] || { label: inv.tipo, color: 'bg-slate-100 text-slate-700' };
                  const statusMeta = STATUS_LABEL[inv.status] || STATUS_LABEL.draft;
                  const StIcon = statusMeta.icon;
                  return (
                    <tr key={inv.id} className="hover:bg-violet-50/40 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${tipoMeta.color}`}>{tipoMeta.label}</span>
                          <span className="font-black text-slate-800">#{inv.numero}</span>
                          <span className="text-xs text-slate-400">/ s{inv.serie}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        {/* iter156af: mostra Nome Fantasia como principal quando disponivel.
                            Razao Social vira secundaria em azul (mesmo padrao da busca no dropdown). */}
                        {inv.customer_trade_name && inv.customer_trade_name !== inv.customer_name ? (
                          <>
                            <p className="font-bold text-slate-800 text-xs truncate">{inv.customer_trade_name}</p>
                            <p className="text-[10px] text-sky-700 font-semibold truncate">{inv.customer_name}</p>
                          </>
                        ) : (
                          <p className="font-bold text-slate-800 text-xs truncate">{inv.customer_name || '— sem cliente —'}</p>
                        )}
                        {inv.customer_doc && <p className="text-[10px] text-slate-400">{inv.customer_doc}</p>}
                      </td>
                      <td className="px-3 py-2.5">
                        <p className="text-xs text-slate-700">{inv.natureza_op_description || '—'}</p>
                        {inv.cfop && <p className="text-[10px] text-slate-400">CFOP {inv.cfop}</p>}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(inv.created_at)}</td>
                      <td className="px-3 py-2.5 text-right font-black text-slate-800">{fmtBRL(inv.total)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${statusMeta.color}`}>
                          <StIcon size={10} /> {statusMeta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <RowActionsMenu
                          inv={inv}
                          onIssue={() => handleIssue(inv)}
                          onEdit={() => { setEditing(inv); setShowForm(true); }}
                          onDuplicate={() => handleDuplicate(inv)}
                          onDelete={() => handleDelete(inv)}
                          onViewDanfe={() => handleViewDanfe(inv)}
                          onDownloadXml={() => handleDownloadXml(inv)}
                          onDiagnose={() => setDiagnosisModal({ invoice: inv })}
                          onCCe={() => setCceModal({ invoice: inv })}
                          onCancel={() => handleCancel(inv)}
                          onConsultStatus={() => handleConsultStatus(inv)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <InvoiceForm
          companyId={companyId}
          invoice={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
      {showInutModal && (
        <InutilizacaoModal
          companyId={companyId}
          onClose={() => setShowInutModal(false)}
          onSaved={() => { setShowInutModal(false); load(); }}
        />
      )}
      {cceModal && (
        <CCeModal
          invoice={cceModal.invoice}
          onClose={() => setCceModal(null)}
          onSaved={() => { setCceModal(null); load(); }}
        />
      )}
      {/* iter77aj: Modal de Configuracoes Faltantes — exibido quando usuario tenta inserir NF-e sem cert/natureza */}
      {configCheck.missing && (
        <ConfigMissingModal
          reasons={configCheck.reasons}
          onClose={() => setConfigCheck(s => ({ ...s, missing: false }))}
        />
      )}
      {/* iter77aj: Modal de Diagnostico de Rejeicao SEFAZ */}
      {diagnosisModal && (
        <DiagnosisModal
          invoice={diagnosisModal.invoice}
          onClose={() => setDiagnosisModal(null)}
          onRetransmit={() => { setDiagnosisModal(null); load(); }}
        />
      )}
    </div>
  );
};


/* iter77aj: ========================= Config Missing Modal ========================= */
const ConfigMissingModal = ({ reasons, onClose }) => {
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="config-missing-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border-2 border-amber-300">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertCircle size={28} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-black text-slate-900">Atencao! Configuracoes para NF-e nao encontradas.</h3>
            <p className="text-sm text-slate-600 mt-1">Configure os itens abaixo antes de emitir notas fiscais:</p>
            <ul className="mt-3 space-y-1.5">
              {reasons.map((r, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                  <span className="font-semibold text-slate-800">{r.label}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-3">Configure em: <strong>Administracao &gt; Config. de NF &gt; NF-e</strong></p>
          </div>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold" data-testid="config-missing-close">Entendi</button>
          <button
            onClick={() => { window.location.href = '/erp/fiscal-config'; }}
            className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold flex items-center gap-2"
            data-testid="config-missing-goto"
          ><Settings size={14} /> Ir para Config Fiscal</button>
        </div>
      </div>
    </div>
  );
};

/* iter77aj: ========================= Diagnosis Modal (Rejeicao SEFAZ) ========================= */
const DiagnosisModal = ({ invoice, onClose, onRetransmit }) => {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reissuing, setReissuing] = useState(false);
  // iter156ee: vincular chave autorizada (cStat 539)
  const [recoverKey, setRecoverKey] = useState('');
  const [recovering, setRecovering] = useState(false);
  const recoverByKey = async () => {
    const chave = recoverKey.replace(/\D/g, '');
    if (chave.length !== 44) { toast.error('Informe os 44 digitos da chave'); return; }
    setRecovering(true);
    const tid = toast.loading('Consultando protocolo na SEFAZ...');
    try {
      const { data } = await axios.post(`${ERP}/invoices/${invoice.id}/recover-by-key`, { chave }, { withCredentials: true, timeout: 60000 });
      toast.dismiss(tid);
      if (data.linked_as_reference) {
        toast.success(data.message || 'Chave vinculada como NF-e referenciada. Clique em Reenviar ao SEFAZ.', { duration: 10000 });
      } else {
        toast.success(`Nota #${data.numero} AUTORIZADA — protocolo ${data.protocolo || '—'}`, { duration: 8000 });
      }
      onRetransmit();
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'SEFAZ nao confirmou a chave informada', { duration: 10000 });
    } finally { setRecovering(false); }
  };
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/invoices/${invoice.id}/diagnosis`, { withCredentials: true });
        setDiag(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao carregar diagnostico');
      } finally { setLoading(false); }
    })();
  }, [invoice.id]);

  const downloadResp = async () => {
    try {
      const { data } = await axios.get(`${ERP}/invoices/${invoice.id}/sefaz-response`, { responseType: 'blob', withCredentials: true });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a'); a.href = url; a.download = `sefaz-resp-${invoice.numero}.xml`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resposta SEFAZ nao disponivel (notas pre-iter77aj nao tem este registro).');
    }
  };

  const retransmit = async () => {
    const ok = await confirmDialog({
      title: 'Reenviar nota ao SEFAZ?',
      message: 'A nota sera retransmitida ao SEFAZ com os mesmos dados atuais.',
      description: 'Use esta opcao apenas apos corrigir o erro indicado no diagnostico.',
      confirmLabel: 'Reenviar',
      variant: 'info',
    });
    if (!ok) return;
    setReissuing(true);
    const tid = toast.loading('Reenviando ao SEFAZ...');
    try {
      const { data } = await axios.post(`${ERP}/invoices/${invoice.id}/issue`, {}, { withCredentials: true, timeout: 60000 });
      toast.dismiss(tid);
      if (data.status === 'issued') {
        toast.success('Autorizada!');
        onRetransmit();
      } else {
        toast.error(`Ainda rejeitada: ${data.sefaz_xmotivo || 'sem motivo'}`, { duration: 8000 });
        // Recarrega diagnostico atualizado
        const { data: newD } = await axios.get(`${ERP}/invoices/${invoice.id}/diagnosis`, { withCredentials: true });
        setDiag(newD);
      }
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'Erro ao reenviar');
    } finally { setReissuing(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="diagnosis-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <AlertCircle size={20} className="text-rose-600" />
            <h3 className="text-lg font-black text-slate-900">Diagnostico de Rejeicao - NF #{invoice.numero}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="diagnosis-close"><X size={18} /></button>
        </div>
        <div className="p-6">
          {loading && <p className="text-slate-500 text-sm">Carregando diagnostico...</p>}
          {diag && diag.has_diagnosis && (
            <>
              <div className="bg-rose-50 border-2 border-rose-200 rounded-xl p-4 mb-4">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-xs font-bold text-rose-700 bg-rose-200 px-2 py-0.5 rounded">cStat {diag.diagnosis.codigo}</span>
                  <span className="text-xs text-rose-600">Ambiente: {diag.ambiente}</span>
                </div>
                <p className="text-base font-black text-rose-900 mb-1">{diag.diagnosis.titulo}</p>
                {diag.diagnosis.motivo && <p className="text-sm text-rose-700 italic">&ldquo;{diag.diagnosis.motivo}&rdquo;</p>}
              </div>
              {/* iter77as: XSD local validation problems (when available) */}
              {Array.isArray(diag.xsd_problems) && diag.xsd_problems.length > 0 && (
                <div className="mb-4 bg-amber-50 border-2 border-amber-300 rounded-lg p-4" data-testid="diagnosis-xsd-panel">
                  <h4 className="text-sm font-black text-amber-900 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <AlertTriangle size={16} className="text-amber-600" />
                    Erros do Schema XML (validacao local)
                  </h4>
                  <p className="text-[11px] text-amber-700 mb-2">Estes sao os problemas que detectamos no XML enviado, comparando com o schema oficial NFe 4.00. Sao a causa MAIS PROVAVEL da rejeicao:</p>
                  <ul className="space-y-1.5">
                    {diag.xsd_problems.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-amber-900 font-mono bg-white border border-amber-200 rounded px-2 py-1.5" data-testid={`xsd-problem-${i}`}>
                        <span className="font-bold flex-shrink-0">#{i + 1}</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* iter77ct.95: Painel de Pagamentos — mostra o que foi ENVIADO vs o que estava SALVO */}
              {(Array.isArray(diag.sent_payments) || Array.isArray(diag.stored_payments)) && (diag.sent_payments?.length || diag.stored_payments?.length) > 0 && (
                <div className="mb-4 bg-slate-50 border-2 border-slate-300 rounded-lg p-4" data-testid="diagnosis-payments-panel">
                  <h4 className="text-sm font-black text-slate-800 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <AlertCircle size={16} className="text-slate-600" />
                    Formas de Pagamento Enviadas
                  </h4>
                  <p className="text-[11px] text-slate-600 mb-3">Isso e EXATAMENTE o que a SEFAZ recebeu. Se aparece &ldquo;Cartao&rdquo; quando voce queria PIX, e um problema de mapeamento no cadastro.</p>

                  {/* Warning de mismatch (destaque vermelho) */}
                  {diag.mismatch_warning && (
                    <div className="mb-3 bg-rose-100 border border-rose-300 rounded-lg p-3" data-testid="diagnosis-mismatch-warning">
                      <p className="text-sm font-bold text-rose-900 mb-1">Diagnostico do problema:</p>
                      <p className="text-xs text-rose-800 leading-relaxed">{diag.mismatch_warning}</p>
                    </div>
                  )}

                  {/* Tabela: o que foi enviado */}
                  {Array.isArray(diag.sent_payments) && diag.sent_payments.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-black text-slate-500 uppercase mb-1.5">Enviado a SEFAZ (do XML)</p>
                      <div className="space-y-1">
                        {diag.sent_payments.map((sp, i) => (
                          <div key={i} className={`flex items-center justify-between px-3 py-1.5 rounded border text-xs font-mono ${sp.is_card_code ? 'bg-rose-50 border-rose-300' : 'bg-white border-slate-200'}`} data-testid={`sent-pay-${i}`}>
                            <span className="flex items-center gap-2">
                              <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${sp.is_card_code ? 'bg-rose-200 text-rose-900' : 'bg-slate-200 text-slate-700'}`}>tPag {sp.t_pag}</span>
                              <span className={sp.is_card_code ? 'text-rose-900 font-bold' : 'text-slate-800'}>{sp.label}</span>
                              {sp.is_card_code && !sp.has_card_group && (
                                <span className="text-[10px] font-bold text-rose-700 uppercase">sem grupo card</span>
                              )}
                            </span>
                            <span className="text-slate-700 font-bold">{fmtBRL(sp.v_pag)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tabela: o que estava salvo no DB (a intencao do usuario) */}
                  {Array.isArray(diag.stored_payments) && diag.stored_payments.length > 0 && (
                    <div>
                      <p className="text-[10px] font-black text-slate-500 uppercase mb-1.5">Salvo na nota (intencao do usuario)</p>
                      <div className="space-y-1">
                        {diag.stored_payments.map((sp, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-1.5 rounded bg-white border border-slate-200 text-xs" data-testid={`stored-pay-${i}`}>
                            <span className="text-slate-700"><span className="font-bold">Metodo:</span> {sp.method || '(vazio)'}</span>
                            <span className="text-slate-700 font-bold">{fmtBRL(sp.value || 0)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="mb-4">
                {/* iter77ax: contador de tentativas + aviso de limite */}
                {(invoice.issue_attempts || invoice.max_attempts_reached) && (
                  <div className={`text-[11px] font-bold uppercase tracking-wider mb-2 px-2 py-1 rounded inline-block ${invoice.max_attempts_reached ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`} data-testid="diagnosis-attempts-badge">
                    Tentativas: {invoice.issue_attempts || 0}/{invoice.max_attempts || 5}
                    {invoice.max_attempts_reached && ' · LIMITE ATINGIDO'}
                  </div>
                )}
              </div>
              {/* iter77ax: passo-a-passo so aparece quando NAO eh fallback generico OU quando ha xsd_problems especificos */}
              {!diag.diagnosis.is_generic && (diag.diagnosis.passos || []).length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-black text-slate-800 uppercase tracking-wider mb-3">Passo-a-passo para resolver</h4>
                  <ol className="space-y-2.5">
                    {(diag.diagnosis.passos || []).map((p, i) => (
                      <li key={i} className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={`diagnosis-step-${i}`}>
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-black flex items-center justify-center">{i + 1}</span>
                        <span className="text-sm text-slate-700 leading-relaxed">{p}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {diag.diagnosis.is_generic && (!Array.isArray(diag.xsd_problems) || diag.xsd_problems.length === 0) && (
                <div className="mb-4 px-3 py-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-500" data-testid="diagnosis-generic-hint">
                  Sem orientacao especifica para este codigo. Baixe a resposta SEFAZ para analise detalhada.
                </div>
              )}
              {/* iter156ee: recuperacao manual por chave autorizada (cStat 539 — nota autorizada na SEFAZ, mas retorno nao chegou) */}
              {/* iter156fa: painel do CSC utilizado (QR-Code) — cStat 463/464/465 */}
              {['463', '464', '465'].includes(String(diag.cstat || invoice.sefaz_cstat)) && (
                <div className="mb-4 p-3 rounded-xl border-2 border-amber-300 bg-amber-50" data-testid="csc-used-box">
                  <h4 className="text-xs font-black text-amber-800 uppercase tracking-wider mb-1">CSC utilizado nesta tentativa</h4>
                  {invoice.csc_used ? (
                    <div className="text-xs text-amber-900 space-y-0.5">
                      <p><b>Origem:</b> {invoice.csc_used.source === 'filial' ? 'configuracao da FILIAL (correto)' : 'configuracao PADRAO do tenant (fallback) — se esta filial tem CNPJ diferente, cadastre o CSC dela'}</p>
                      <p><b>CSC ID:</b> {invoice.csc_used.csc_id || '(vazio)'} · <b>Token:</b> {invoice.csc_used.token_len || 0} caracteres, final ...{invoice.csc_used.token_tail || '????'}{invoice.csc_used.has_inner_space ? ' · ATENCAO: token contem espaco interno' : ''}</p>
                      <p><b>CNPJ emitente:</b> {invoice.csc_used.cnpj || '—'}</p>
                      <p className="text-amber-700 mt-1">Compare com o CSC ativo no portal da SEFAZ para este CNPJ (ambiente de producao). Se houver diferenca, corrija em Configuracoes &gt; Fiscal &gt; NFC-e e reenvie.</p>
                    </div>
                  ) : (
                    <p className="text-xs text-amber-800">Reenvie ao SEFAZ para o sistema registrar qual CSC foi usado; em seguida compare com o CSC ativo no portal da SEFAZ para este CNPJ.</p>
                  )}
                </div>
              )}
              <div className={`mb-4 p-3 rounded-xl border-2 ${String(diag.cstat || invoice.sefaz_cstat) === '539' ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-testid="recover-by-key-box">
                <p className="text-xs font-black text-slate-800 uppercase tracking-wide">{String(invoice.finalidade) === '4' ? 'Devolucao: chave da nota do fornecedor ou chave autorizada' : 'Nota ja autorizada na SEFAZ? Vincule a chave'}</p>
                <p className="text-[11px] text-slate-600 mt-1">
                  {String(diag.cstat || invoice.sefaz_cstat) === '539'
                    ? 'A rejeicao 539 significa que a SEFAZ JA AUTORIZOU esta nota numa transmissao anterior (com outra chave). Consulte a chave no portal da SEFAZ, cole abaixo e o sistema confirma o protocolo e atualiza a nota para Autorizada.'
                    : String(invoice.finalidade) === '4'
                      ? 'Cole a chave (44 digitos) da NF-e que o FORNECEDOR emitiu contra a sua empresa: o sistema valida o digito e vincula como NF-e referenciada desta devolucao (sem consultar a SEFAZ — notas antigas nao sao consultaveis). Se colar a chave desta propria nota ja autorizada, ele reconcilia o protocolo.'
                      : 'Se esta nota consta como autorizada no portal da SEFAZ, cole a chave de acesso (44 digitos) para reconciliar.'}
                </p>
                {String(invoice.finalidade) === '4' && (invoice.ref_nfe_keys || []).length > 0 && (
                  <p className="text-[10px] text-emerald-700 font-bold mt-1" data-testid="diag-ref-keys">NF-e referenciada(s): {(invoice.ref_nfe_keys || []).join(', ')}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-2">
                  <input value={recoverKey} onChange={e => setRecoverKey(e.target.value)} placeholder="Chave de acesso (44 digitos — pode colar com pontos/tracos)"
                    className="flex-1 min-w-[260px] px-3 py-2 border-2 border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:border-emerald-500" data-testid="recover-key-input" />
                  <button onClick={recoverByKey} disabled={recovering || recoverKey.replace(/\D/g, '').length !== 44}
                    className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold flex items-center gap-2" data-testid="recover-key-submit">
                    <RefreshCw size={14} className={recovering ? 'animate-spin' : ''} /> {recovering ? 'Consultando SEFAZ...' : 'Vincular e consultar'}
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">{recoverKey.replace(/\D/g, '').length}/44 digitos</p>
              </div>
              <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
                {diag.raw_response_available && (
                  <button onClick={downloadResp} className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold flex items-center gap-2" data-testid="diagnosis-download-resp">
                    <Download size={14} /> Baixar resposta SEFAZ
                  </button>
                )}
                <button onClick={retransmit} disabled={reissuing || invoice.max_attempts_reached} className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="diagnosis-retransmit" title={invoice.max_attempts_reached ? 'Limite de 5 tentativas atingido — inutilize e refaca' : ''}>
                  <Send size={14} /> {reissuing ? 'Reenviando...' : (invoice.max_attempts_reached ? 'Limite atingido' : 'Reenviar ao SEFAZ')}
                </button>
                <button onClick={onClose} className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-bold" data-testid="diagnosis-edit">Fechar</button>
              </div>
            </>
          )}
          {diag && !diag.has_diagnosis && (
            <p className="text-slate-500 text-sm">{diag.message || 'Diagnostico nao disponivel.'}</p>
          )}
        </div>
      </div>
    </div>
  );
};


/* ========================= TabMoreActionsButton — Inutilizar + Baixar XMLs (iter77bd) ========================= */
const TabMoreActionsButton = ({ filterTipo, onInutilizar, companyId }) => {
  const [open, setOpen] = useState(false);
  const [showXmlsModal, setShowXmlsModal] = useState(false);
  const wrapperRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const downloadXmlsCurrentMonth = async () => {
    const tid = toast.loading('Gerando ZIP dos XMLs do mes atual...');
    try {
      const today = new Date();
      const start = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      const end = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
      const params = new URLSearchParams({ start, end });
      if (filterTipo) params.set('tipo', filterTipo);
      if (companyId) params.set('company_id', companyId);
      const { data: blob } = await axios.get(`${ERP}/invoices/export-xmls?${params.toString()}`, { responseType: 'blob', withCredentials: true });
      toast.dismiss(tid);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xmls_${filterTipo || 'todos'}_${start}_a_${end}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado');
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.status === 404 ? 'Nenhum XML no periodo' : 'Erro ao baixar XMLs');
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="px-3 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg font-bold text-xs flex items-center gap-1.5"
        data-testid="tab-more-actions-btn"
        title="Mais acoes"
      >
        <Settings size={14} /> Mais Acoes <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute z-40 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl min-w-[240px] py-1" data-testid="tab-more-actions-menu">
          {filterTipo !== '' && (
            <button
              type="button"
              onClick={() => { onInutilizar(); setOpen(false); }}
              className="w-full px-3 py-2 flex items-center gap-2 text-xs hover:bg-amber-50 text-amber-700"
              data-testid="action-inutilizar"
            >
              <Ban size={13} /> Inutilizar Numeracao
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              const ok = await confirmDialog({
                title: 'Excluir TODAS as notas em HOMOLOGACAO?',
                message: 'Esta acao limpa resquicios de testes. Notas em PRODUCAO nao sao afetadas.',
                description: 'Use antes de comecar a emitir em producao para zerar a numeracao do ambiente de homologacao.',
                confirmLabel: 'Limpar Homologacao',
                danger: true,
              });
              if (!ok) { setOpen(false); return; }
              const tid = toast.loading('Limpando notas de homologacao...');
              try {
                const { data } = await axios.post(`${ERP}/invoices/purge-homologacao`, {}, { withCredentials: true });
                toast.dismiss(tid);
                toast.success(`${data.deleted || 0} nota(s) de homologacao removidas`);
                window.location.reload();
              } catch (err) {
                toast.dismiss(tid);
                toast.error(err.response?.data?.detail || 'Erro ao limpar');
              }
              setOpen(false);
            }}
            className="w-full px-3 py-2 flex items-center gap-2 text-xs hover:bg-rose-50 text-rose-700"
            data-testid="action-purge-homol"
            title="Limpar TODAS as notas em homologacao (para iniciar producao com numeracao zerada)"
          >
            <Trash2 size={13} /> Limpar notas HOMOLOGACAO
          </button>
          <button
            type="button"
            onClick={downloadXmlsCurrentMonth}
            className="w-full px-3 py-2 flex items-center gap-2 text-xs hover:bg-violet-50 text-violet-700"
            data-testid="action-download-xmls-month"
          >
            <Download size={13} /> Baixar XMLs do Mes
          </button>
          <button
            type="button"
            onClick={() => { setShowXmlsModal(true); setOpen(false); }}
            className="w-full px-3 py-2 flex items-center gap-2 text-xs hover:bg-violet-50 text-violet-700"
            data-testid="action-download-xmls-period"
          >
            <Download size={13} /> Baixar XMLs de um Periodo...
          </button>
        </div>
      )}
      {showXmlsModal && (
        <XmlsPeriodModal
          filterTipo={filterTipo}
          companyId={companyId}
          onClose={() => setShowXmlsModal(false)}
        />
      )}
    </div>
  );
};

/* ========================= XmlsPeriodModal — picker de periodo (iter77bd) ========================= */
const XmlsPeriodModal = ({ filterTipo, companyId, onClose }) => {
  const today = new Date();
  const _ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [start, setStart] = useState(_ymd(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [end, setEnd] = useState(_ymd(today));
  const [tipo, setTipo] = useState(filterTipo || '');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    const tid = toast.loading('Gerando ZIP...');
    try {
      const params = new URLSearchParams({ start, end });
      if (tipo) params.set('tipo', tipo);
      if (companyId) params.set('company_id', companyId);
      const { data: blob } = await axios.get(`${ERP}/invoices/export-xmls?${params.toString()}`, { responseType: 'blob', withCredentials: true });
      toast.dismiss(tid);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xmls_${tipo || 'todos'}_${start}_a_${end}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado');
      onClose();
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.status === 404 ? 'Nenhum XML no periodo' : 'Erro ao baixar XMLs');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="xmls-period-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-black text-slate-800 flex items-center gap-2"><Download size={16} className="text-violet-600" /> Baixar XMLs de um Periodo</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="xmls-modal-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">Data Inicial</label>
              <input type="date" value={start} onChange={e => setStart(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg" data-testid="xmls-modal-start" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">Data Final</label>
              <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg" data-testid="xmls-modal-end" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">Tipo</label>
            <select value={tipo} onChange={e => setTipo(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg" data-testid="xmls-modal-tipo">
              <option value="">Todas (NF-e + NFC-e)</option>
              <option value="nfe">NF-e (Modelo 55)</option>
              <option value="nfce">NFC-e (Modelo 65)</option>
            </select>
          </div>
        </div>
        <div className="bg-slate-50 px-5 py-3 flex items-center justify-end gap-2 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-2 rounded-lg bg-slate-200 text-slate-700 text-sm font-bold" data-testid="xmls-modal-cancel">Cancelar</button>
          <button onClick={submit} disabled={loading} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-bold flex items-center gap-1.5" data-testid="xmls-modal-submit">
            <Download size={14} /> {loading ? 'Gerando...' : 'Baixar ZIP'}
          </button>
        </div>
      </div>
    </div>
  );
};


/* ========================= PaymentAddRow — linha de "Adicionar forma" (iter77bc) ========================= */
/* iter156et: Importar vendas para compor uma NF-e/NFC-e (multi-selecao, filtros por numero/cliente/data) */
const ImportSalesModal = ({ companyId, onClose, onImport }) => {
  const _today = new Date().toISOString().slice(0, 10);
  const _monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [filterBy, setFilterBy] = useState('client');
  const [filterValue, setFilterValue] = useState('');
  const [dateFrom, setDateFrom] = useState(_monthAgo);
  const [dateTo, setDateTo] = useState(_today);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams({ company_id: companyId || '', type: 'venda', status: 'completed', limit: '200', date_from: dateFrom, date_to: dateTo });
      if (filterValue.trim()) { qp.set('filter_by', filterBy); qp.set('filter_value', filterValue.trim()); }
      const { data } = await axios.get(`${ERP}/sales?${qp.toString()}`, { withCredentials: true });
      setSales(data.data || []);
    } catch { setSales([]); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filterBy, filterValue, dateFrom, dateTo]);
  useEffect(() => { search(); }, [search]);

  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectedSales = sales.filter(s => selected.has(s.id));
  const selTotal = selectedSales.reduce((a, s) => a + (Number(s.total) || 0), 0);
  const distinctClients = new Set(selectedSales.map(s => s.client_id || s.customer_name || '')).size;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" data-testid="import-sales-modal">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="bg-slate-900 text-white px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2"><ShoppingCart size={18} className="text-cyan-300" /><div><p className="text-[10px] uppercase tracking-widest opacity-70 font-bold">Compor nota</p><p className="text-base font-black">Importar vendas</p></div></div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg" data-testid="import-sales-close"><X size={18} /></button>
        </div>
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 grid grid-cols-1 md:grid-cols-[130px_minmax(0,1fr)_auto_auto_auto] gap-2 items-end">
          <div>
            <label className={labelCls}>Buscar por</label>
            <select value={filterBy} onChange={e => setFilterBy(e.target.value)} className={inputClass} data-testid="import-sales-filter-by">
              <option value="client">Cliente</option>
              <option value="number">Nº da venda</option>
              <option value="product">Produto</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Valor</label>
            <div className="relative"><Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" /><input value={filterValue} onChange={e => setFilterValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), search())} placeholder={filterBy === 'number' ? 'Ex.: 1372' : filterBy === 'client' ? 'Nome ou CPF/CNPJ' : 'Nome ou SKU'} className={inputClass + ' pl-8'} data-testid="import-sales-filter-value" /></div>
          </div>
          <div><label className={labelCls}>De</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputClass} data-testid="import-sales-date-from" /></div>
          <div><label className={labelCls}>Até</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={inputClass} data-testid="import-sales-date-to" /></div>
          <button type="button" onClick={search} className="h-9 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold flex items-center gap-1.5" data-testid="import-sales-search"><Search size={14} /> Buscar</button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <p className="p-8 text-center text-sm text-slate-400">Buscando vendas...</p>
            : sales.length === 0 ? <p className="p-8 text-center text-sm text-slate-400">Nenhuma venda concluída encontrada no período.</p>
            : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 sticky top-0"><tr>
                  <th className="px-3 py-2 w-8"><input type="checkbox" checked={sales.length > 0 && selected.size === sales.length} onChange={e => setSelected(e.target.checked ? new Set(sales.map(s => s.id)) : new Set())} data-testid="import-sales-select-all" /></th>
                  <th className="px-2 py-2 text-left">Venda</th><th className="px-2 py-2 text-left">Data</th><th className="px-2 py-2 text-left">Cliente</th><th className="px-2 py-2 text-left">Pagamento</th><th className="px-2 py-2 text-right">Itens</th><th className="px-3 py-2 text-right">Total</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {sales.map(s => {
                    const pays = Array.isArray(s.payments) && s.payments.length ? s.payments.map(p => p.label || p.method).join(' + ') : (s.payment_method || '—');
                    const on = selected.has(s.id);
                    return (
                      <tr key={s.id} onClick={() => toggle(s.id)} className={`cursor-pointer transition-colors ${on ? 'bg-violet-50' : 'hover:bg-slate-50'}`} data-testid={`import-sale-row-${s.id}`}>
                        <td className="px-3 py-2"><input type="checkbox" checked={on} onChange={() => toggle(s.id)} onClick={e => e.stopPropagation()} data-testid={`import-sale-check-${s.id}`} /></td>
                        <td className="px-2 py-2 font-black text-slate-800">#{s.number}</td>
                        <td className="px-2 py-2 text-slate-600 text-xs">{fmtDate(s.created_at)}</td>
                        <td className="px-2 py-2 text-slate-800 truncate max-w-[200px]">{s.customer_name || 'Consumidor'}</td>
                        <td className="px-2 py-2 text-xs text-slate-600 capitalize">{pays}</td>
                        <td className="px-2 py-2 text-right text-slate-600">{(s.items || []).length}</td>
                        <td className="px-3 py-2 text-right font-bold text-slate-900">{fmtBRL(s.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 bg-white flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-slate-600" data-testid="import-sales-summary">
            <strong>{selected.size}</strong> venda(s) · <strong>{fmtBRL(selTotal)}</strong>
            {distinctClients > 1 && <span className="ml-2 text-amber-700 font-semibold inline-flex items-center gap-1"><AlertTriangle size={12} /> clientes diferentes — o cliente ficará em branco</span>}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button type="button" disabled={selected.size === 0} onClick={() => onImport(selectedSales)} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white text-sm font-bold flex items-center gap-1.5" data-testid="import-sales-confirm"><Check size={14} /> Importar {selected.size > 0 ? `${selected.size} venda(s)` : ''}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};


const PaymentAddRow = ({ paymentMethods, suggestedValue, suggestedDate, onAdd, inputClass, labelCls }) => {
  const [method, setMethod] = useState('');
  const [value, setValue] = useState(suggestedValue);
  const [date, setDate] = useState(suggestedDate);

  // Atualiza sugestoes quando o saldo restante muda externamente
  useEffect(() => { setValue(suggestedValue); }, [suggestedValue]);
  useEffect(() => { setDate(suggestedDate); }, [suggestedDate]);

  // iter156dt5: "Sem Pagamento" (tPag=90 SEFAZ) — transferencia entre filiais, doacao, brinde.
  // Detecta via type ou nome do metodo cadastrado. Quando selecionado, valor sempre = 0.
  const selectedPm = paymentMethods.find(pm => (pm.type || pm.name) === method);
  const isSemPagamento = (() => {
    if (!method) return false;
    const key = String(method).toLowerCase();
    if (key === 'sem_pagamento' || key === '90' || key.includes('sem pag') || key.includes('cortesia')) return true;
    if (selectedPm && (selectedPm.type === 'sem_pagamento' || String(selectedPm.name || '').toLowerCase().includes('cortesia') || String(selectedPm.name || '').toLowerCase().includes('sem pag'))) return true;
    return false;
  })();
  useEffect(() => {
    if (isSemPagamento) setValue(0);
  }, [isSemPagamento]);

  const canAdd = !!method && (isSemPagamento || (Number(value) || 0) > 0);
  const submit = () => {
    if (!canAdd) return;
    onAdd({ method, value: isSemPagamento ? 0 : (Number(value) || 0), due_date: date });
    setMethod('');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end" data-testid="invoice-payment-add-row">
      <div className="md:col-span-4">
        <label className={labelCls}>Forma de Pagamento <span className="text-rose-500">*</span></label>
        <select value={method} onChange={e => setMethod(e.target.value)} className={inputClass} data-testid="payment-add-method">
          <option value="">Selecione</option>
          {paymentMethods.map(pm => <option key={pm.id} value={pm.type || pm.name}>{pm.name}</option>)}
        </select>
        {isSemPagamento && (
          <p className="text-[10px] text-amber-700 font-bold mt-1">
            SEFAZ tPag 90 — valor sera enviado como R$ 0,00 (nota sem pagamento, ex: transferencia entre filiais)
          </p>
        )}
      </div>
      <div className="md:col-span-3">
        <label className={labelCls}>Valor {!isSemPagamento && <span className="text-rose-500">*</span>}</label>
        <BRLInput value={isSemPagamento ? 0 : value} onChange={(v) => setValue(v)} className={inputClass} dataTestid="payment-add-value" disabled={isSemPagamento} />
      </div>
      <div className="md:col-span-3">
        <label className={labelCls}>Data do Vencimento {!isSemPagamento && <span className="text-rose-500">*</span>}</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputClass} data-testid="payment-add-date" disabled={isSemPagamento} />
      </div>
      <div className="md:col-span-2">
        <button
          type="button"
          onClick={submit}
          disabled={!canAdd}
          className="w-full h-10 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold flex items-center justify-center gap-1.5 text-sm shadow"
          data-testid="payment-add-btn"
        >
          <Plus size={14} /> Adicionar
        </button>
      </div>
    </div>
  );
};


/* ========================= BRLInput — input numerico que formata BRL ========================= */
const BRLInput = ({ value, onChange, className, dataTestid, placeholder, disabled }) => {
  // iter77ba: armazena number, exibe formato "1,00". On focus mostra raw; on blur formata.
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState(() => (value === '' || value == null ? '' : String(value).replace('.', ',')));
  useEffect(() => {
    if (!focused) setRaw(value === '' || value == null ? '' : Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  }, [value, focused]);
  const display = focused ? raw : (value === '' || value == null ? '' : Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      onFocus={() => { setFocused(true); setRaw(value === '' || value == null ? '' : String(value).replace('.', ',')); }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const v = e.target.value.replace(/[^\d.,-]/g, '').replace('.', ',');
        setRaw(v);
        const num = parseFloat(v.replace(/\./g, '').replace(',', '.'));
        onChange(Number.isFinite(num) ? num : 0);
      }}
      className={className}
      data-testid={dataTestid}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
};


/* ========================= RowActionsMenu — split-button + dropdown contextual ========================= */
const RowActionsMenu = ({ inv, onIssue, onEdit, onDuplicate, onDelete, onViewDanfe, onDownloadXml, onDiagnose, onCCe, onCancel, onConsultStatus }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // iter77ct.32: {top, right} do menu em coord de viewport (fixed)
  const wrapperRef = React.useRef(null);
  const toggleBtnRef = React.useRef(null);
  const menuRef = React.useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // iter77ct.32: fecha em scroll/resize para nao ficar orfao. Recalcular seria overkill.
  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  // iter77ct.32: calcula posicao do menu quando abre para evitar clipping pelo overflow-hidden da table.
  const handleToggle = () => {
    if (open) { setOpen(false); return; }
    if (toggleBtnRef.current) {
      const rect = toggleBtnRef.current.getBoundingClientRect();
      // Estima altura do menu (~ 220px para 6 itens). Se nao houver espaco abaixo, abre PARA CIMA.
      const menuH = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < menuH && rect.top > menuH;
      setMenuPos({
        top: openUpward ? Math.max(8, rect.top - 4) : rect.bottom + 4,
        right: Math.max(8, window.innerWidth - rect.right),
        transform: openUpward ? 'translateY(-100%)' : 'none',
      });
    }
    setOpen(true);
  };

  const isFiscal = inv.tipo === 'nfe' || inv.tipo === 'nfce';

  // Define primary action + dropdown items by status
  let primary = null;
  const items = [];
  if (inv.status === 'draft') {
    if (isFiscal) {
      primary = {
        label: `Enviar${inv.issue_attempts ? ` (${inv.issue_attempts}/5)` : ''}`,
        icon: Send,
        disabled: inv.max_attempts_reached,
        cls: inv.max_attempts_reached ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600 text-white',
        onClick: onIssue,
        testid: `invoice-issue-${inv.id}`,
      };
    } else {
      primary = { label: 'Editar', icon: FileText, cls: 'bg-violet-500 hover:bg-violet-600 text-white', onClick: onEdit, testid: `invoice-edit-${inv.id}` };
    }
    items.push({ label: 'Editar', icon: FileText, onClick: onEdit, testid: `invoice-edit-${inv.id}`, cls: 'text-slate-700' });
    items.push({ label: 'Duplicar', icon: Copy, onClick: onDuplicate, testid: `invoice-duplicate-${inv.id}`, cls: 'text-violet-600' });
    items.push({ label: 'Excluir', icon: Trash2, onClick: onDelete, testid: `invoice-delete-${inv.id}`, cls: 'text-rose-600' });
  } else if (inv.status === 'issued') {
    primary = { label: 'Imprimir DANFE', icon: Printer, cls: 'bg-sky-500 hover:bg-sky-600 text-white', onClick: onViewDanfe, testid: `invoice-danfe-${inv.id}` };
    items.push({ label: 'Baixar XML', icon: Download, onClick: onDownloadXml, testid: `invoice-xml-${inv.id}`, cls: 'text-slate-700' });
    if (inv.tipo === 'nfe') items.push({ label: 'Carta Correcao', icon: FileEdit, onClick: onCCe, testid: `invoice-cce-${inv.id}`, cls: 'text-amber-600' });
    items.push({ label: 'Consultar Status', icon: AlertCircle, onClick: onConsultStatus, testid: `invoice-consult-${inv.id}`, cls: 'text-sky-600' });
    items.push({ label: 'Duplicar', icon: Copy, onClick: onDuplicate, testid: `invoice-duplicate-${inv.id}`, cls: 'text-violet-600' });
    items.push({ label: 'Cancelar Nota', icon: XCircle, onClick: onCancel, testid: `invoice-cancel-${inv.id}`, cls: 'text-rose-600' });
  } else if (inv.status === 'rejected') {
    primary = { label: 'Diagnostico', icon: AlertCircle, cls: 'bg-rose-500 hover:bg-rose-600 text-white', onClick: onDiagnose, testid: `invoice-diagnose-${inv.id}` };
    items.push({ label: 'Editar e Reenviar', icon: Edit, onClick: onEdit, testid: `invoice-edit-${inv.id}`, cls: 'text-blue-600' });
    items.push({
      label: inv.max_attempts_reached ? 'Reenviar (LIMITE)' : `Reenviar SEFAZ${inv.issue_attempts ? ` (${inv.issue_attempts}/5)` : ''}`,
      icon: Send,
      onClick: onIssue,
      disabled: inv.max_attempts_reached,
      testid: `invoice-issue-${inv.id}`,
      cls: inv.max_attempts_reached ? 'text-slate-400 cursor-not-allowed' : 'text-emerald-600',
    });
    items.push({ label: 'Consultar Status', icon: AlertCircle, onClick: onConsultStatus, testid: `invoice-consult-${inv.id}`, cls: 'text-sky-600' });
    if (inv.xml) items.push({ label: 'Baixar XML enviado', icon: Download, onClick: onDownloadXml, testid: `invoice-xml-${inv.id}`, cls: 'text-slate-700' });
    items.push({ label: 'Duplicar', icon: Copy, onClick: onDuplicate, testid: `invoice-duplicate-${inv.id}`, cls: 'text-violet-600' });
  } else if (inv.status === 'pending') {
    primary = { label: 'Consultar SEFAZ', icon: AlertCircle, cls: 'bg-amber-500 hover:bg-amber-600 text-white', onClick: onConsultStatus, testid: `invoice-consult-${inv.id}` };
    items.push({
      label: `Reenviar SEFAZ${inv.issue_attempts ? ` (${inv.issue_attempts}/5)` : ''}`,
      icon: Send,
      onClick: onIssue,
      disabled: inv.max_attempts_reached,
      testid: `invoice-issue-${inv.id}`,
      cls: inv.max_attempts_reached ? 'text-slate-400 cursor-not-allowed' : 'text-emerald-600',
    });
    if (inv.xml) items.push({ label: 'Baixar XML', icon: Download, onClick: onDownloadXml, testid: `invoice-xml-${inv.id}`, cls: 'text-slate-700' });
    items.push({ label: 'Duplicar', icon: Copy, onClick: onDuplicate, testid: `invoice-duplicate-${inv.id}`, cls: 'text-violet-600' });
  } else { // cancelled
    primary = { label: 'Duplicar', icon: Copy, cls: 'bg-violet-500 hover:bg-violet-600 text-white', onClick: onDuplicate, testid: `invoice-duplicate-${inv.id}` };
    if (inv.xml) items.push({ label: 'Baixar XML', icon: Download, onClick: onDownloadXml, testid: `invoice-xml-${inv.id}`, cls: 'text-slate-700' });
  }

  // iter77be: Excluir notas em HOMOLOGACAO (qualquer status) — limpa resquicios de testes.
  const isHomol = (inv.sefaz_ambiente || '').toLowerCase() === 'homologacao' || (inv.sefaz_ambiente || '').toLowerCase() === 'homol' || inv.sefaz_ambiente === '2';
  if (isHomol && inv.status !== 'draft') {
    items.push({
      label: 'Excluir (HOMOL)',
      icon: Trash2,
      onClick: onDelete,
      testid: `invoice-delete-homol-${inv.id}`,
      cls: 'text-rose-600',
    });
  }

  const PIcon = primary?.icon;
  return (
    <div className="relative inline-flex items-stretch justify-end" ref={wrapperRef} data-testid={`row-actions-${inv.id}`}>
      <button
        type="button"
        onClick={primary?.onClick}
        disabled={primary?.disabled}
        title={primary?.label}
        className={`px-2.5 py-1.5 rounded-l-lg text-[11px] font-bold flex items-center gap-1.5 ${primary?.cls || 'bg-slate-100 text-slate-700'}`}
        data-testid={primary?.testid}
      >
        {PIcon && <PIcon size={12} />} {primary?.label}
      </button>
      <button
        type="button"
        onClick={handleToggle}
        ref={toggleBtnRef}
        className={`px-1.5 py-1.5 rounded-r-lg border-l border-white/30 text-[11px] font-bold ${primary?.cls || 'bg-slate-100 text-slate-700'}`}
        title="Mais acoes"
        data-testid={`row-actions-toggle-${inv.id}`}
      >
        <ChevronDown size={12} />
      </button>
      {open && items.length > 0 && menuPos && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-xl min-w-[200px] py-1"
          style={{ top: menuPos.top, right: menuPos.right, transform: menuPos.transform }}
          data-testid={`row-actions-menu-${inv.id}`}
        >
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <button
                key={i}
                type="button"
                onClick={() => { if (!it.disabled) { it.onClick(); setOpen(false); } }}
                disabled={it.disabled}
                className={`w-full px-3 py-2 flex items-center gap-2 text-[12px] hover:bg-slate-50 ${it.cls} ${it.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={it.testid}
              >
                <Icon size={13} /> {it.label}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};


/* ========================= InvoiceForm — modal ========================= */
const InvoiceForm = ({ companyId, invoice, onClose, onSaved }) => {
  const isEdit = !!invoice?.id;
  const [tipo, setTipo] = useState(invoice?.tipo || 'nfe');
  const [naturezaId, setNaturezaId] = useState(invoice?.natureza_op_id || '');
  const [naturezas, setNaturezas] = useState([]);
  // iter77av: 5 campos operacionais fiscais (pre-preenchidos pela Natureza, editaveis pelo usuario).
  const [tipoEmissao, setTipoEmissao] = useState(invoice?.tipo_emissao || '');
  const [finalidade, setFinalidade] = useState(invoice?.finalidade || '');
  const [refNfeKeys, setRefNfeKeys] = useState((invoice?.ref_nfe_keys || []).join('\n'));  // iter156fa: NF-e referenciadas (devolucao/complementar)
  const [identificacaoOperacao, setIdentificacaoOperacao] = useState(invoice?.identificacao_operacao || '');
  const [tipoConsumidor, setTipoConsumidor] = useState(invoice?.tipo_consumidor || '');
  const [presencaComprador, setPresencaComprador] = useState(invoice?.presenca_comprador || '');
  // Track when user manually overrides — so natureza auto-fill nao reescreve.
  const [opUserTouched, setOpUserTouched] = useState({ tipo_emissao: !!invoice?.tipo_emissao, finalidade: !!invoice?.finalidade, identificacao_operacao: !!invoice?.identificacao_operacao, tipo_consumidor: !!invoice?.tipo_consumidor, presenca_comprador: !!invoice?.presenca_comprador });
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerName, setCustomerName] = useState(invoice?.customer_name || '');
  // iter156af: nome fantasia snapshot — exibido no titulo do card e na listagem.
  // NAO substitui customer_name (que vai para o XML da NF-e conforme regra SEFAZ).
  const [customerTradeName, setCustomerTradeName] = useState(invoice?.customer_trade_name || '');
  const [customerDoc, setCustomerDoc] = useState(invoice?.customer_doc || '');
  const [customerEmail, setCustomerEmail] = useState(invoice?.customer_email || '');
  const [customerAddress, setCustomerAddress] = useState(invoice?.customer_address || '');
  const [clientId, setClientId] = useState(invoice?.client_id || '');
  // iter77ak: client autocomplete
  const [clientSearch, setClientSearch] = useState(invoice?.customer_name || '');
  const [showClientList, setShowClientList] = useState(false);
  const [items, setItems] = useState(invoice?.items?.length ? invoice.items : []);
  const [discount, setDiscount] = useState(invoice?.discount || 0);
  const [surcharge, setSurcharge] = useState(invoice?.surcharge || 0);
  // iter77bc: campo frete (vFrete no XML — afeta vNF). Default 0.
  const [freight, setFreight] = useState(invoice?.freight || 0);
  // iter77ba+bc: multiplas formas de pagamento (substitui paymentMethod unico, com fallback).
  // Default empty — usuario adiciona via PaymentAddRow.
  // iter156db: se paymentLines vem vazio MAS existe `payment_method` + `total` legado
  // (nota gerada a partir de venda antes do iter156db), reconstroi 1 linha automatica.
  const [paymentLines, setPaymentLines] = useState(() => {
    if (Array.isArray(invoice?.payments) && invoice.payments.length > 0) return invoice.payments;
    if (invoice?.payment_method && (Number(invoice?.total) || 0) > 0) {
      return [{ method: invoice.payment_method, value: Number(invoice.total) || 0 }];
    }
    return [];
  });
  // iter77ax: data de vencimento + toggle de ajustes (desconto/acrescimo)
  // iter77ba: due_date default = hoje (YYYY-MM-DD) se nao definido no invoice.
  const _todayISO = new Date().toISOString().slice(0, 10);
  const [dueDate, setDueDate] = useState(invoice?.due_date || _todayISO);
  const [showAdjustments, setShowAdjustments] = useState(Boolean(invoice?.discount || invoice?.surcharge));
  const [notes, setNotes] = useState(invoice?.notes || '');
  const [transporte, setTransporte] = useState(invoice?.transporte || {
    modFrete: '9', transp_xnome: '', transp_cnpj: '', transp_cpf: '', transp_ie: '',
    transp_xender: '', transp_xmun: '', transp_uf: '', transp_antt: '',
    veic_placa: '', veic_uf: '',
    vol_qtd: '', vol_especie: '', vol_marca: '', vol_num: '', vol_peso_b: '', vol_peso_l: '',
  });
  const [showTransporte, setShowTransporte] = useState(
    // iter77ct.58: para NF-e, por padrao ja abre com "Informar dados de transporte" TICADO.
    // Se ja tinha transporte na invoice, tambem abre. Simplifica o fluxo do usuario.
    !!invoice?.transporte || tipo === 'nfe'
  );
  // iter77ct.57: dropdown de transportadoras cadastradas (fornecedores com is_transportadora=true)
  const [transportadoras, setTransportadoras] = useState([]);
  const [selectedTransportadoraId, setSelectedTransportadoraId] = useState('');
  const [showTranspSuggestions, setShowTranspSuggestions] = useState(false);
  const [showNewTransportadora, setShowNewTransportadora] = useState(false);
  const [newTranspForm, setNewTranspForm] = useState({ name: '', document: '', transp_ie: '', transp_uf: '', transp_endereco: '', transp_municipio: '', transp_antt: '' });
  const [creatingTransp, setCreatingTransp] = useState(false);
  const loadTransportadoras = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ERP}/suppliers?is_transportadora=true`, { withCredentials: true });
      setTransportadoras(data.data || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadTransportadoras(); }, [loadTransportadoras]);
  // Ao escolher uma transportadora, popula os campos automaticamente
  const applyTransportadora = (id) => {
    setSelectedTransportadoraId(id);
    if (!id) return;
    const t = transportadoras.find(x => x.id === id);
    if (!t) return;
    const docDigits = (t.document || '').replace(/\D/g, '');
    setTransporte(prev => ({
      ...prev,
      transp_xnome: t.name || '',
      transp_cnpj: docDigits.length > 11 ? docDigits : '',
      transp_cpf: docDigits.length <= 11 ? docDigits : '',
      transp_ie: t.transp_ie || '',
      transp_xender: t.transp_endereco || t.address || '',
      transp_xmun: t.transp_municipio || '',
      transp_uf: (t.transp_uf || '').toUpperCase().slice(0, 2),
      transp_antt: t.transp_antt || '',
    }));
    toast.success(`Transportadora ${t.name} selecionada`);
  };
  const createTransportadora = async () => {
    if (!newTranspForm.name.trim()) { toast.error('Informe a razao social'); return; }
    setCreatingTransp(true);
    try {
      const payload = {
        name: newTranspForm.name.trim(),
        doc_type: 'cnpj',
        document: (newTranspForm.document || '').replace(/\D/g, ''),
        is_transportadora: true,
        transp_ie: newTranspForm.transp_ie || '',
        transp_uf: (newTranspForm.transp_uf || '').toUpperCase().slice(0, 2),
        transp_endereco: newTranspForm.transp_endereco || '',
        transp_municipio: newTranspForm.transp_municipio || '',
        transp_antt: newTranspForm.transp_antt || '',
      };
      const { data } = await axios.post(`${ERP}/suppliers`, payload, { withCredentials: true });
      toast.success('Transportadora cadastrada!');
      await loadTransportadoras();
      // seleciona a recem criada
      setTimeout(() => applyTransportadora(data.id), 200);
      setShowNewTransportadora(false);
      setNewTranspForm({ name: '', document: '', transp_ie: '', transp_uf: '', transp_endereco: '', transp_municipio: '', transp_antt: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cadastrar transportadora');
    } finally {
      setCreatingTransp(false);
    }
  };
  // iter77ct.59: lookup CNPJ para o modal de nova transportadora — auto-preenche razao/endereco/UF
  const [newTranspLookingUp, setNewTranspLookingUp] = useState(false);
  const lookupTranspCnpj = async () => {
    const digits = (newTranspForm.document || '').replace(/\D/g, '');
    if (digits.length !== 14) { toast.info('Digite um CNPJ com 14 digitos'); return; }
    setNewTranspLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/lookup/cnpj/${digits}`, { withCredentials: true });
      const enderecoLinha = [data.logradouro, data.numero, data.bairro].filter(Boolean).join(', ');
      setNewTranspForm(prev => ({
        ...prev,
        name: prev.name || data.razao_social || data.nome_fantasia || '',
        document: data.cnpj || digits,
        transp_endereco: prev.transp_endereco || enderecoLinha,
        transp_municipio: data.municipio || prev.transp_municipio,
        transp_uf: (data.uf || prev.transp_uf || '').toUpperCase().slice(0, 2),
      }));
      toast.success(`Dados de ${data.razao_social || 'transportadora'} carregados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao consultar CNPJ');
    } finally {
      setNewTranspLookingUp(false);
    }
  };
  // iter77bc: aba ativa entre Produtos e Transporte (apenas NF-e usa Transporte)
  const [productsTab, setProductsTab] = useState('produtos');
  const [saving, setSaving] = useState(false);
  const [searchProd, setSearchProd] = useState('');
  const [activeProdIdx, setActiveProdIdx] = useState(0);
  // iter77ak: fiscal quick-edit
  const [fiscalEditIdx, setFiscalEditIdx] = useState(null);
  // iter77ap: entry row state (pre-add). User picks product, ajusta qty/preco, e SO entao clica "+".
  const [entryProduct, setEntryProduct] = useState(null);
  const [entryQty, setEntryQty] = useState(1);
  const [entryPrice, setEntryPrice] = useState(0);
  // iter77aq: payment methods (loaded from /payment-methods?scope=sales)
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showImportSales, setShowImportSales] = useState(false);  // iter156et: compor nota a partir de vendas
  // iter156ew: natureza padrao configurada em Config. da Empresa > PDV (pdv_default_natop_id) — usada ao importar vendas
  const [defaultNatopId, setDefaultNatopId] = useState('');
  useEffect(() => {
    let alive = true;
    axios.get(`${ERP}/company-settings${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true })
      .then(({ data }) => { if (alive) setDefaultNatopId(data?.pdv_default_natop_id || ''); })
      .catch(() => {});
    return () => { alive = false; };
  }, [companyId]);
  // iter77aq: quick-create client modal — agora suporta {mode: 'create'|'edit', client?} iter77ar
  const [showQuickClient, setShowQuickClient] = useState(null);
  // iter156ac: cadastro rapido de PRODUTO direto na emissao NF-e (fluxo espelho do QuickClientModal)
  const [showQuickProduct, setShowQuickProduct] = useState(false);
  // iter77ct.48: rastreia se o usuario editou manualmente o campo "Observacoes / Informacoes
  // Complementares" (state `notes`). Se nao editou, trocar a NatOp auto-preenche esse
  // campo com o info_complementares configurado na NatOp (envio no payload como info_complementares).
  const [notesTouched, setNotesTouched] = useState(!!invoice?.notes || !!invoice?.info_complementares);

  useEffect(() => {
    (async () => {
      try {
        // iter77ct.55: passa company_id no /clients — respeita visibilidade por filial
        // (mesmo comportamento da tela de Cadastros > Clientes). Antes, sem company_id,
        // podia ainda faltar cliente recem-criado porque o cache do modal era stale.
        const clientsUrl = companyId ? `${ERP}/clients?company_id=${companyId}` : `${ERP}/clients`;
        // iter156h: passa company_id no /natureza-operacao — antes trazia TODAS as naturezas do
        // tenant, incluindo as nao compartilhadas com a filial atual. Agora respeita o
        // shared_company_ids/company_id do share.
        const natopUrl = companyId
          ? `${ERP}/natureza-operacao?active=true&tipo=saida&company_id=${companyId}`
          : `${ERP}/natureza-operacao?active=true&tipo=saida`;
        const [n, c, pm] = await Promise.all([
          axios.get(natopUrl, { withCredentials: true }),
          axios.get(clientsUrl, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
          axios.get(`${ERP}/payment-methods?scope=invoices`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
        ]);
        let natList = n.data.data || [];
        // iter77ct.48: se a nota tem natureza_op_id que NAO esta na lista (inativa, tipo diferente),
        // busca a natureza individualmente e prepende para o dropdown mostrar corretamente.
        const savedNid = invoice?.natureza_op_id;
        if (savedNid && !natList.some(x => x.id === savedNid)) {
          try {
            const { data: extra } = await axios.get(`${ERP}/natureza-operacao/${savedNid}`, { withCredentials: true });
            if (extra && extra.id) {
              natList = [{ ...extra, _legacy: true }, ...natList];
            }
          } catch { /* natop deletada — ignora, dropdown fica sem selecao */ }
        }
        setNaturezas(natList);
        setClients(c.data.data || []);
        setPaymentMethods(pm.data.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  // iter77aq: reload clients after creating one via quick form
  const refreshClients = async () => {
    try {
      // iter77ct.55: passa company_id para respeitar visibilidade por filial
      const url = companyId ? `${ERP}/clients?company_id=${companyId}` : `${ERP}/clients`;
      const { data } = await axios.get(url, { withCredentials: true });
      setClients(data.data || []);
    } catch { /* ignore */ }
  };

  // iter77av: quando a Natureza muda, pre-preenche os 5 campos operacionais SE o usuario ainda nao
  // tiver mexido naquele campo. Tambem cobre NFC-e (defaults proprios) e auto-deteccao por CPF.
  const selectedNatop = useMemo(() => naturezas.find(n => n.id === naturezaId), [naturezas, naturezaId]);
  useEffect(() => {
    if (!selectedNatop) return;
    if (!opUserTouched.tipo_emissao && selectedNatop.tipo_emissao) setTipoEmissao(String(selectedNatop.tipo_emissao));
    if (!opUserTouched.finalidade && selectedNatop.finalidade) setFinalidade(String(selectedNatop.finalidade));
    if (!opUserTouched.identificacao_operacao && selectedNatop.identificacao_operacao) setIdentificacaoOperacao(String(selectedNatop.identificacao_operacao));
    if (!opUserTouched.tipo_consumidor && selectedNatop.tipo_consumidor != null) setTipoConsumidor(String(selectedNatop.tipo_consumidor));
    if (!opUserTouched.presenca_comprador && selectedNatop.presenca_comprador != null) setPresencaComprador(String(selectedNatop.presenca_comprador));
    // iter77ct.48: auto-preenche o campo "Observacoes / Informacoes Complementares" (state `notes`)
    // com o info_complementares configurado na NatOp — apenas se o usuario ainda nao editou.
    if (!notesTouched && selectedNatop.info_complementares) {
      setNotes(selectedNatop.info_complementares);
    }
  }, [selectedNatop]);

  // iter77av: NFC-e sempre tem defaults proprios (1=Final, 1=Presencial) — pre-preenche se vazios.
  useEffect(() => {
    if (tipo === 'nfce') {
      if (!opUserTouched.tipo_consumidor) setTipoConsumidor('1');
      if (!opUserTouched.presenca_comprador) setPresencaComprador('1');
    }
  }, [tipo]);

  // iter77av: auto-detecta CPF destinatario e forca tipo_consumidor=1 (Consumidor Final).
  // Regra SEFAZ NFe 4.00: CPF nao-contribuinte requer indFinal=1.
  useEffect(() => {
    const d = (customerDoc || '').replace(/\D/g, '');
    if (d.length === 11 && !opUserTouched.tipo_consumidor) {
      setTipoConsumidor('1');
    }
  }, [customerDoc]);

  // iter77ap: when editing an existing invoice, re-hydrate fiscal data for items that have
  // product_id but no fiscal snapshot (notas criadas antes da iter77ak). Busca os produtos
  // e merge fiscal silenciosamente para que NCM/CST/etc apareçam pre-preenchidos.
  useEffect(() => {
    if (!isEdit) return;
    const needHydration = (invoice?.items || []).some(it => it.product_id && (!it.ncm || !it.icms_cst));
    if (!needHydration) return;
    (async () => {
      try {
        const ids = [...new Set((invoice?.items || []).filter(it => it.product_id).map(it => it.product_id))];
        const byId = {};
        await Promise.all(ids.map(async (pid) => {
          try {
            // iter77ap: fetch product via /api/products/{id} (returns full fiscal block).
            const { data } = await axios.get(`${API}/products/${pid}`, { withCredentials: true });
            if (data && data.id) byId[pid] = data;
          } catch { /* skip */ }
        }));
        setItems(prev => prev.map(it => {
          if (!it.product_id || !byId[it.product_id]) return it;
          const p = byId[it.product_id];
          const f = p.fiscal || {};
          return {
            ...it,
            // Mantem os valores ja salvos (qty, unit_price, name) e completa apenas o fiscal.
            unit_price: Number(it.unit_price) || Number(p.price) || 0,
            ncm: it.ncm || f.ncm || '',
            cest: it.cest || f.cest || '',
            cfop: it.cfop || f.cfop_default || '',
            origem: it.origem || f.origem || '0',
            unit_commercial: it.unit_commercial || f.unit_commercial || '',
            icms_cst: it.icms_cst || f.icms_cst || '',
            icms_aliquota: it.icms_aliquota ?? f.icms_aliquota ?? null,
            pis_cst: it.pis_cst || f.pis_cst || '',
            cofins_cst: it.cofins_cst || f.cofins_cst || '',
          };
        }));
      } catch { /* ignore */ }
    })();
  }, []);

  // Lazy product search
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!searchProd || searchProd.length < 2) { setProducts([]); return; }
      try {
        const { data } = await axios.get(`${ERP}/pdv/products?q=${encodeURIComponent(searchProd)}&limit=20${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true });
        setProducts(data.data || []);
        setActiveProdIdx(0);
      } catch { setProducts([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchProd, companyId]);

  const subtotal = useMemo(() =>
    items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0)
  , [items]);
  const total = subtotal - (Number(discount) || 0) + (Number(surcharge) || 0) + (Number(freight) || 0);

  const addProduct = (p) => {
    setItems(prev => {
      const idx = prev.findIndex(it => it.product_id === p.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: (Number(copy[idx].quantity) || 0) + 1 };
        return copy;
      }
      // iter77ak: snapshot fiscal data from product master
      const f = p.fiscal || {};
      // iter156cr: se ha natureza selecionada, ela sobrescreve o CFOP/tributos do produto
      // (assim quando o usuario troca a natureza depois, ja veem os valores certos).
      const nn = naturezas.find(n => n.id === naturezaId);
      const natCfop = nn?.cfop || '';
      return [...prev, {
        product_id: p.id,
        name: p.name,
        sku: p.sku || '',
        quantity: 1,
        unit_price: Number(p.price) || 0,
        ncm: f.ncm || '',
        cest: f.cest || '',
        cfop: natCfop || f.cfop_default || '',
        origem: f.origem || '0',
        unit_commercial: f.unit_commercial || '',
        icms_cst: (nn?.icms?.cst) || f.icms_cst || '',
        icms_aliquota: (nn?.icms?.aliquota ?? f.icms_aliquota) ?? null,
        icms_aliquota_st: (nn?.icms?.aliquota_st ?? f.icms_aliquota_st) ?? null,
        icms_mva: (nn?.icms?.mva ?? f.icms_mva) ?? null,
        icms_red_bc: (nn?.icms?.red_bc ?? f.icms_red_bc) ?? null,
        pis_cst: (nn?.pis?.cst) || f.pis_cst || '',
        pis_aliquota: (nn?.pis?.aliquota ?? f.pis_aliquota) ?? null,
        cofins_cst: (nn?.cofins?.cst) || f.cofins_cst || '',
        cofins_aliquota: (nn?.cofins?.aliquota ?? f.cofins_aliquota) ?? null,
        ipi_cst: (nn?.ipi?.cst) || f.ipi_cst || '',
        ipi_aliquota: (nn?.ipi?.aliquota ?? f.ipi_aliquota) ?? null,
      }];
    });
    setSearchProd('');
    setProducts([]);
  };

  const addEmptyLine = () => {
    setItems(prev => [...prev, { product_id: null, name: '', sku: '', quantity: 1, unit_price: 0, ncm: '', cfop: '', origem: '0' }]);
  };

  const updItem = (idx, k, v) => {
    setItems(prev => {
      const copy = [...prev];
      const isNum = (k === 'quantity' || k === 'unit_price' || k === 'icms_aliquota' || k === 'pis_aliquota' || k === 'cofins_aliquota');
      copy[idx] = { ...copy[idx], [k]: isNum ? (v === '' ? null : Number(v) || 0) : v };
      return copy;
    });
  };

  const rmItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // iter77ak: Client autocomplete
  // iter77ct.55: se o filtro local NAO encontra, faz busca no backend (tenant inteiro)
  // — pega clientes recem-criados ou vinculados a outras filiais. Merge local + remoto.
  const [remoteClients, setRemoteClients] = useState([]);
  useEffect(() => {
    const q = clientSearch.trim();
    if (q.length < 2) { setRemoteClients([]); return; }
    // Se ja tem match local, evita chamada desnecessaria
    const qLower = q.toLowerCase();
    const localMatch = clients.some(c => {
      const name = (c.name || '').toLowerCase();
      const doc = (c.document || '').toLowerCase();
      const phone = (c.phone || '').toLowerCase();
      return name.includes(qLower) || doc.includes(qLower) || phone.includes(qLower);
    });
    if (localMatch) { setRemoteClients([]); return; }
    const t = setTimeout(async () => {
      try {
        // Busca SEM company_id — pega qualquer cliente do tenant que corresponda
        const { data } = await axios.get(`${ERP}/clients?search=${encodeURIComponent(q)}`, { withCredentials: true });
        setRemoteClients(data.data || []);
      } catch { setRemoteClients([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [clientSearch, clients]);

  const filteredClients = useMemo(() => {
    // iter156ae: busca prioriza NOME FANTASIA (trade_name), depois razao social (legal_name/name).
    // Normalizacao SEM acentos.
    const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const q = norm(clientSearch.trim());
    if (!q) return clients.slice(0, 30);
    // Marca matches: prioridade 1 = trade_name, 2 = legal_name/name, 3 = document/phone
    const scored = [];
    for (const c of clients) {
      const trade = norm(c.trade_name);
      const legal = norm(c.legal_name);
      const name = norm(c.name);
      const doc = norm(c.document);
      const phone = norm(c.phone);
      let score = 0;
      if (trade.includes(q)) score = 3;
      else if (legal.includes(q) || name.includes(q)) score = 2;
      else if (doc.includes(q) || phone.includes(q)) score = 1;
      if (score > 0) scored.push([score, c]);
    }
    if (scored.length > 0) {
      scored.sort((a, b) => b[0] - a[0]);
      return scored.slice(0, 30).map(([, c]) => c);
    }
    // Fallback: usa resultados remotos quando local nao encontra
    return remoteClients.slice(0, 30);
  }, [clients, clientSearch, remoteClients]);

  const pickClient = (cli) => {
    setClientId(cli.id);
    // iter156ae: customerName vai para a NF-e — sempre a razao social oficial (name/legal_name).
    // ClientSearch (o que o operador ve no input) mostra Nome Fantasia se PJ tem trade_name.
    setCustomerName(cli.name || cli.legal_name || '');
    // iter156af: snapshot do Nome Fantasia (para exibir na listagem/titulo do card, sem alterar XML)
    setCustomerTradeName(cli.trade_name || '');
    setCustomerDoc(cli.document || '');
    setCustomerEmail(cli.email || cli.email_nfe || '');
    // iter77am: cadastro de cliente agora usa endereco fiscal estruturado (end_*) como unico endereco.
    // Compose a partir dos campos fiscais; fallback para o legacy `cli.address` quando disponivel.
    const fiscalAddr = [cli.end_logradouro, cli.end_numero, cli.end_complemento, cli.end_bairro, cli.end_municipio, cli.end_uf, cli.end_cep].filter(Boolean).join(', ');
    setCustomerAddress(fiscalAddr || [cli.address, cli.city, cli.state].filter(Boolean).join(', '));
    const isPJWithFantasy = cli.doc_type === 'cnpj' && cli.trade_name && cli.trade_name.trim();
    setClientSearch(isPJWithFantasy ? cli.trade_name : (cli.name || ''));
    setShowClientList(false);
  };

  // iter156et: importa 1+ vendas: cliente (se unico), itens (com dados fiscais do cadastro), pagamento (se igual em todas), desconto/acrescimo
  const importSales = async (sales) => {
    if (!sales || sales.length === 0) return;
    // iter156ev: `products` so contem o resultado da busca -> buscar dados fiscais (NCM/CFOP/CST) do cadastro por ids
    const ids = [...new Set(sales.flatMap(sl => (sl.items || []).map(it => it.product_id)).filter(id => id && String(id).length === 24))];
    let fiscalById = {};
    if (ids.length) {
      try {
        const { data } = await axios.get(`${ERP}/pdv/products?ids=${ids.join(',')}&limit=${ids.length}${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true });
        fiscalById = Object.fromEntries((data.data || []).map(pr => [pr.id, pr]));
      } catch { fiscalById = {}; }
    }
    const clientKeys = new Set(sales.map(sl => sl.client_id || sl.customer_name || ''));
    if (clientKeys.size === 1) {
      const first = sales[0];
      const cli = first.client_id ? clients.find(c => c.id === first.client_id) : null;
      if (cli) pickClient(cli);
      else if (first.customer_name) { setClientId(first.client_id || ''); setCustomerName(first.customer_name); setCustomerDoc(first.customer_doc || ''); setClientSearch(first.customer_name); }
    } else {
      clearClient();
      toast.warning('As vendas selecionadas sao de clientes diferentes — informe o cliente da nota manualmente.');
    }
    const prodById = { ...Object.fromEntries((products || []).map(pr => [pr.id, pr])), ...fiscalById };
    const newItems = [];
    let semNcm = 0;
    sales.forEach(sl => (sl.items || []).forEach(it => {
      const pr = prodById[it.product_id] || {};
      const f = { ...(pr.fiscal || {}) };
      if (!f.ncm && (pr.ncm || it.ncm)) f.ncm = pr.ncm || it.ncm;
      if (!f.ncm) semNcm += 1;
      newItems.push({
        product_id: it.product_id || null,
        name: it.name || pr.name || '',
        sku: it.sku || pr.sku || '',
        quantity: Number(it.quantity) || 1,
        unit_price: Number(it.unit_price) || 0,
        ncm: it.ncm || f.ncm || '',
        cest: f.cest || '',
        cfop: f.cfop_default || '',
        origem: f.origem || '0',
        unit_commercial: f.unit_commercial || '',
        icms_cst: f.icms_cst || '',
        icms_aliquota: f.icms_aliquota ?? null,
        pis_cst: f.pis_cst || '',
        cofins_cst: f.cofins_cst || '',
        sale_number: sl.number,
      });
    }));
    setItems(prev => [...prev, ...newItems]);
    const totDisc = sales.reduce((a, sl) => a + (Number(sl.discount) || 0), 0);
    const totSur = sales.reduce((a, sl) => a + (Number(sl.surcharge) || 0), 0);
    if (totDisc > 0) setDiscount(d => Number(d || 0) + totDisc);
    if (totSur > 0) setSurcharge(d => Number(d || 0) + totSur);
    if (totDisc > 0 || totSur > 0) setShowAdjustments(true);
    // Pagamento: se TODAS as vendas usam a mesma forma unica -> preenche com a soma; senao deixa em aberto
    const methodOf = (sl) => {
      const ps = Array.isArray(sl.payments) ? sl.payments.filter(pp => Number(pp.amount) > 0) : [];
      if (ps.length === 1) return (ps[0].method || '').toLowerCase();
      if (ps.length === 0) return (sl.payment_method || '').toLowerCase();
      return null;
    };
    const methods = new Set(sales.map(methodOf));
    const only = methods.size === 1 ? [...methods][0] : null;
    if (only) {
      const pm = paymentMethods.find(x => (x.type || x.name || '').toLowerCase() === only) || paymentMethods.find(x => (x.type || '').toLowerCase() === only);
      const key = pm ? (pm.type || pm.name) : only;
      const sum = sales.reduce((a, sl) => a + (Number(sl.total) || 0), 0);
      setPaymentLines([{ method: key, value: Math.round(sum * 100) / 100, due_date: _todayISO }]);
    } else {
      setPaymentLines([]);
      toast.info('Formas de pagamento diferentes entre as vendas — informe a forma de pagamento da nota.');
    }
    // Natureza de operacao: usa a padrao das vendas (config PDV) se ainda nao escolhida
    if (!naturezaId && defaultNatopId && naturezas.some(nt => nt.id === defaultNatopId)) setNaturezaId(defaultNatopId);
    const nums = sales.map(sl => `#${sl.number}`).join(', ');
    setNotes(n => (n ? `${n}\n` : '') + `Ref. venda(s) ${nums}`);
    setShowImportSales(false);
    toast.success(`${sales.length} venda(s) importada(s): ${newItems.length} item(ns)`);
    if (semNcm > 0) toast.warning(`${semNcm} item(ns) sem NCM no cadastro do produto — preencha o NCM na aba Fiscal do produto ou direto na nota.`, { duration: 8000 });
  };

  const clearClient = () => {
    setClientId('');
    setClientSearch('');
    setShowClientList(false);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveProdIdx(i => Math.min(i + 1, products.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveProdIdx(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') {
      if (products[activeProdIdx]) { e.preventDefault(); addProduct(products[activeProdIdx]); }
    } else if (e.key === 'Escape') { setSearchProd(''); setProducts([]); }
  };

  // iter77ak: Pre-flight validation
  const preflightIssues = useMemo(() => {
    const issues = [];
    if (!items.length) issues.push({ level: 'error', msg: 'Adicione ao menos um item.' });
    if ((tipo === 'nfe' || tipo === 'nfce') && !naturezaId) issues.push({ level: 'error', msg: 'Selecione a Natureza de Operacao (CFOP nao definido).' });
    if (tipo === 'nfe' && !customerDoc) issues.push({ level: 'error', msg: 'NF-e (mod 55) exige CPF ou CNPJ do destinatario.' });
    if (tipo === 'nfe' && !customerName) issues.push({ level: 'error', msg: 'Nome do destinatario obrigatorio para NF-e.' });
    items.forEach((it, idx) => {
      if (!it.name) issues.push({ level: 'error', msg: `Item #${idx + 1}: descricao obrigatoria.` });
      if (!(Number(it.quantity) > 0)) issues.push({ level: 'error', msg: `Item #${idx + 1}: quantidade invalida.` });
      if (!(Number(it.unit_price) >= 0)) issues.push({ level: 'error', msg: `Item #${idx + 1}: preco invalido.` });
      // Fiscal alerts apply only to NF-e / NFC-e
      if (tipo === 'nfe' || tipo === 'nfce') {
        const ncm = (it.ncm || '').replace(/\D/g, '');
        if (!ncm) issues.push({ level: 'error', msg: `Item #${idx + 1} (${it.name || 'sem nome'}): NCM obrigatorio — SEFAZ rejeitara sem este campo.` });
        else if (ncm.length !== 8) issues.push({ level: 'error', msg: `Item #${idx + 1}: NCM deve ter 8 digitos (atual: ${ncm.length}).` });
        // iter77aq: ICMS CST eh um warning, nao error — o backend cai para o CST da natureza se ausente.
        if (!it.icms_cst) issues.push({ level: 'warn', msg: `Item #${idx + 1}: CST/CSOSN do ICMS sera herdado da natureza de operacao (sem override).` });
        // CFOP override consistency
        if (it.cfop && !/^\d{4}$/.test(String(it.cfop))) issues.push({ level: 'error', msg: `Item #${idx + 1}: CFOP invalido (4 digitos).` });
      }
    });
    // Tipo-specific destinatario
    if (tipo === 'nfce' && customerDoc) {
      const d = customerDoc.replace(/\D/g, '');
      if (d && d.length !== 11 && d.length !== 14) issues.push({ level: 'warn', msg: 'CPF/CNPJ deve ter 11 ou 14 digitos.' });
    }
    return issues;
  }, [items, tipo, naturezaId, customerDoc, customerName]);

  const hasBlockingIssues = preflightIssues.some(i => i.level === 'error');

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (hasBlockingIssues) { toast.error('Preencha os campos obrigatorios marcados com *.'); return; }
    setSaving(true);
    try {
      const payload = {
        tipo,
        company_id: companyId,
        natureza_op_id: naturezaId || null,
        // iter77av: 5 campos operacionais fiscais
        tipo_emissao: tipoEmissao || null,
        finalidade: finalidade || null,
        ref_nfe_keys: refNfeKeys.split(/[\s,;]+/).map(k => k.replace(/\D/g, '')).filter(k => k.length === 44),
        identificacao_operacao: identificacaoOperacao || null,
        tipo_consumidor: tipoConsumidor || null,
        presenca_comprador: presencaComprador || null,
        client_id: clientId || null,
        customer_name: customerName,
        customer_trade_name: customerTradeName || null,  // iter156af: snapshot para listagem
        customer_doc: customerDoc,
        customer_email: customerEmail,
        customer_address: customerAddress,
        items,
        discount: Number(discount) || 0,
        surcharge: Number(surcharge) || 0,
        freight: Number(freight) || 0,
        // iter77ba: payment_method = forma da 1a linha (backward compat); payments = array completo.
        payment_method: (paymentLines[0]?.method) || '',
        payments: paymentLines.filter(p => p.method).map(p => ({
          method: p.method,
          value: Number(p.value) || 0,
          due_date: p.due_date || null,
        })),
        due_date: dueDate || null,
        notes,
        // iter77ct.48: o campo do rodape "Observacoes / Informacoes Complementares" (state `notes`)
        // e o texto que vai para a tag infCpl do XML (visivel no DANFE). Enviado tambem como
        // info_complementares para o SEFAZ. Se o usuario nao editou, o backend puxa da NatOp
        // automaticamente via _attach_natop_snapshot (regra: nao sobrescreve se ja preenchido).
        info_complementares: notes || '',
        transporte: showTransporte ? transporte : null,
      };
      if (isEdit) {
        await axios.put(`${ERP}/invoices/${invoice.id}`, payload, { withCredentials: true });
        toast.success('Nota atualizada');
      } else {
        await axios.post(`${ERP}/invoices`, payload, { withCredentials: true });
        toast.success('Nota criada (rascunho)');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="invoice-form-modal">
      <div className="bg-white w-full md:max-w-5xl md:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
        <div className="bg-gradient-to-r from-violet-600 to-cyan-500 px-5 py-4 text-white flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{isEdit ? 'Editar' : 'Nova'} Nota</p>
            <p className="text-xl font-black">{isEdit ? `${TIPO_LABEL[tipo]?.label || tipo} #${invoice?.numero}` : 'Emissao de Documento Fiscal'}</p>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setShowImportSales(true)} className="p-2 hover:bg-white/20 rounded-lg relative" title="Importar vendas para compor esta nota" data-testid="invoice-import-sales-btn">
              <ShoppingCart size={20} />
              <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-amber-400 text-slate-900 text-[9px] font-black flex items-center justify-center">+</span>
            </button>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="invoice-form-close"><X /></button>
          </div>
        </div>
        {showImportSales && <ImportSalesModal companyId={companyId} onClose={() => setShowImportSales(false)} onImport={importSales} />}

        <form onSubmit={handleSubmit} className="overflow-y-auto p-5 space-y-5 flex-1">
          {/* iter77aw: Card unificado — Tipo + Natureza + Operacao Fiscal (5 selects) em um so quadrante */}
          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-black text-slate-500 uppercase tracking-wider">Operacao</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Tipo de Documento</label>
                <select value={tipo} onChange={e => setTipo(e.target.value)} className={inputClass} disabled={isEdit} data-testid="invoice-tipo">
                  <option value="nfe">NF-e (modelo 55)</option>
                  <option value="nfce">NFC-e (modelo 65)</option>
                  {(tipo === 'avulsa' || tipo === 'cupom') && (
                    <option value={tipo}>{tipo === 'avulsa' ? 'Avulsa (legado)' : 'Cupom (legado)'}</option>
                  )}
                </select>
              </div>
              <div>
                <label className={labelCls}>Natureza de Operacao {(tipo === 'nfe' || tipo === 'nfce') && <span className="text-rose-500">*</span>}</label>
                <select value={naturezaId} onChange={e => {
                  const newNatId = e.target.value;
                  setNaturezaId(newNatId);
                  // iter156cr: quando a natureza muda, propaga CFOP + tributos para TODOS os
                  // itens ja adicionados. Sem isso, o CFOP override do produto (ex: 6401) fica
                  // "carimbado" no item mesmo com natureza 5401 selecionada — confunde o usuario
                  // e forcava o backend (iter156cq) a converter em runtime.
                  const nn = naturezas.find(n => n.id === newNatId);
                  if (nn) {
                    const _n = {
                      cfop: nn.cfop || '',
                      icms_cst: nn.icms?.cst || '',
                      icms_aliquota: nn.icms?.aliquota ?? null,
                      icms_aliquota_st: nn.icms?.aliquota_st ?? null,
                      icms_mva: nn.icms?.mva ?? null,
                      icms_red_bc: nn.icms?.red_bc ?? null,
                      pis_cst: nn.pis?.cst || '',
                      pis_aliquota: nn.pis?.aliquota ?? null,
                      cofins_cst: nn.cofins?.cst || '',
                      cofins_aliquota: nn.cofins?.aliquota ?? null,
                      ipi_cst: nn.ipi?.cst || '',
                      ipi_aliquota: nn.ipi?.aliquota ?? null,
                    };
                    setItems(prev => prev.map(it => ({ ...it, ..._n })));
                  }
                }} className={inputClass} data-testid="invoice-natop">
                  <option value="">— Selecione —</option>
                  {naturezas.map(n => <option key={n.id} value={n.id}>{n.code} · {n.description} · CFOP {n.cfop}{n._legacy ? ' · (inativa)' : ''}</option>)}
                </select>
                <p className="text-[10px] text-slate-400 mt-1">CFOP/CST/aliquotas e Informacoes Complementares serao puxados desta natureza ou do produto.</p>
              </div>
            </div>

            {/* iter77av+aw: Operacao Fiscal — 5 selects encostados logo abaixo da Natureza, no mesmo card */}
            {(tipo === 'nfe' || tipo === 'nfce') && (
              <div className="pt-3 border-t border-slate-100" data-testid="invoice-fiscal-op-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Detalhes da Operacao Fiscal</p>
                  {selectedNatop && (
                    <span className="text-[10px] text-slate-400">Pre-preenchido pela <strong className="text-violet-600">{selectedNatop.description}</strong></span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <div>
                    <label className={labelCls}>Tipo Emissao</label>
                    <select value={tipoEmissao} onChange={e => { setTipoEmissao(e.target.value); setOpUserTouched(t => ({ ...t, tipo_emissao: true })); }} className={inputClass} data-testid="invoice-tipo-emissao">
                      <option value="">— Padrao —</option>
                      <option value="normal">1 · Normal</option>
                      <option value="contingencia_fsda">2 · FS-DA</option>
                      <option value="contingencia_svc_an">6 · SVC-AN</option>
                      <option value="contingencia_svc_rs">7 · SVC-RS</option>
                      <option value="contingencia_offline">9 · Offline NFC-e</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Finalidade</label>
                    <select value={finalidade} onChange={e => { setFinalidade(e.target.value); setOpUserTouched(t => ({ ...t, finalidade: true })); }} className={inputClass} data-testid="invoice-finalidade">
                      <option value="">— Padrao —</option>
                      <option value="1">1 · Normal</option>
                      <option value="2">2 · Complementar</option>
                      <option value="3">3 · Ajuste</option>
                      <option value="4">4 · Devolucao</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Identif. Oper.</label>
                    <select value={identificacaoOperacao} onChange={e => { setIdentificacaoOperacao(e.target.value); setOpUserTouched(t => ({ ...t, identificacao_operacao: true })); }} className={inputClass} data-testid="invoice-id-dest">
                      <option value="">— Auto (UF) —</option>
                      <option value="1">1 · Interna</option>
                      <option value="2">2 · Interestadual</option>
                      <option value="3">3 · Exterior</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>
                      Tipo Consumidor
                      {(customerDoc || '').replace(/\D/g, '').length === 11 && (
                        <span className="ml-1 text-[9px] text-emerald-600 font-bold">(auto CPF=Final)</span>
                      )}
                    </label>
                    <select value={tipoConsumidor} onChange={e => { setTipoConsumidor(e.target.value); setOpUserTouched(t => ({ ...t, tipo_consumidor: true })); }} className={inputClass} data-testid="invoice-tipo-consumidor">
                      <option value="">— Padrao —</option>
                      <option value="0">0 · Normal</option>
                      <option value="1">1 · Consumidor Final</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Presenca</label>
                    <select value={presencaComprador} onChange={e => { setPresencaComprador(e.target.value); setOpUserTouched(t => ({ ...t, presenca_comprador: true })); }} className={inputClass} data-testid="invoice-presenca">
                      <option value="">— Padrao —</option>
                      <option value="0">0 · N/A</option>
                      <option value="1">1 · Presencial</option>
                      <option value="2">2 · Internet</option>
                      <option value="3">3 · Tele</option>
                      <option value="4">4 · Domicilio</option>
                      <option value="9">9 · Outros</option>
                    </select>
                  </div>
                </div>
                {(() => {
                  const DEVOL = ['201','202','208','209','210','410','411','412','413','503','553','555','556','660','661','662','918','919','921'];
                  const cf = String(selectedNatop?.cfop || '');
                  const isDevolCfop = cf.length === 4 && DEVOL.includes(cf.slice(1));
                  const show = finalidade === '4' || finalidade === '2' || isDevolCfop;
                  if (!show) return null;
                  const keys = refNfeKeys.split(/[\s,;]+/).map(k => k.replace(/\D/g, '')).filter(Boolean);
                  const bad = keys.filter(k => k.length !== 44);
                  return (
                    <div className="mt-3 p-3 rounded-xl border-2 border-amber-300 bg-amber-50" data-testid="invoice-ref-nfe-box">
                      <label className={`${labelCls} text-amber-900`}>NF-e referenciada — chave de acesso (44 digitos) {isDevolCfop && finalidade !== '4' && <span className="ml-1 text-[10px] font-bold text-rose-700">CFOP {cf} e de devolucao: a nota sera enviada com finalidade 4</span>}</label>
                      <textarea value={refNfeKeys} onChange={e => setRefNfeKeys(e.target.value)} rows={2} placeholder="Cole a chave da NF-e original (uma por linha para varias)" className={inputClass + ' font-mono text-xs'} data-testid="invoice-ref-nfe-keys" />
                      <p className="text-[10px] text-amber-800 mt-1">Obrigatorio para devolucao (finalidade 4) e complementar (2): a SEFAZ rejeita sem a referencia (Rej. 328/321). {keys.length > 0 && <strong>{keys.length - bad.length} chave(s) valida(s){bad.length ? ` · ${bad.length} invalida(s)` : ''}</strong>}</p>
                    </div>
                  );
                })()}
                <p className="text-[10px] text-slate-400 mt-1.5">CPF no destinatario forca <strong>Consumidor Final = 1</strong> (regra SEFAZ).</p>
              </div>
            )}
          </div>

          {/* Cliente — autocomplete iter77ak + quick create/edit iter77aq + lembrete cadastro incompleto iter77ar */}
          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-black text-slate-500 uppercase tracking-wider">Destinatario</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2 relative">
                <label className={labelCls}>
                  <span className="flex items-center gap-2">
                    Cliente {tipo === 'nfe' && <span className="text-rose-500">*</span>}
                    <button
                      type="button"
                      onClick={() => setShowQuickClient({ mode: 'create' })}
                      className="p-0.5 rounded bg-violet-600 hover:bg-violet-700 text-white"
                      title="Cadastrar novo cliente"
                      data-testid="invoice-client-add-btn"
                    ><Plus size={11} /></button>
                    {clientId && (
                      <button
                        type="button"
                        onClick={async () => {
                          // iter77ct.76: quando o cliente selecionado NAO esta no `clients` local
                          // (ex: lista filtrada / paginada, ou fetch sem esse cliente), busca via API.
                          let target = clients.find(c => c.id === clientId);
                          if (!target || !target.name) {
                            try {
                              const { data } = await axios.get(`${ERP}/clients/${clientId}`, { withCredentials: true });
                              target = data;
                            } catch (err) {
                              toast.error('Nao foi possivel carregar dados do cliente para edicao');
                              return;
                            }
                          }
                          setShowQuickClient({ mode: 'edit', client: target });
                        }}
                        className="p-0.5 rounded text-violet-600 hover:bg-violet-50"
                        title="Editar cadastro do cliente selecionado"
                        data-testid="invoice-client-edit-btn"
                      ><Edit size={11} /></button>
                    )}
                  </span>
                </label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={clientSearch}
                    onChange={e => { setClientSearch(e.target.value); setShowClientList(true); if (clientId) clearClient(); }}
                    onFocus={() => setShowClientList(true)}
                    onBlur={() => setTimeout(() => setShowClientList(false), 200)}
                    placeholder={tipo === 'nfce' ? 'Busque o cliente (Nome Fantasia, CPF/CNPJ) — ou deixe vazio' : 'Busque por Nome Fantasia, Razao Social, CPF/CNPJ ou telefone'}
                    className={`${inputClass} pl-9 pr-20`}
                    data-testid="invoice-client-search"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {clientId && (
                      <button type="button" onClick={clearClient} className="text-[10px] font-bold text-rose-500 hover:bg-rose-50 px-2 py-1 rounded" data-testid="invoice-client-clear">Limpar</button>
                    )}
                    <ChevronDown size={14} className="text-slate-400" />
                  </div>
                </div>
                {showClientList && filteredClients.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto" data-testid="invoice-client-dropdown">
                    {filteredClients.map(c => {
                      // iter156ae: PJ com Nome Fantasia -> mostra fantasia como titulo, razao social secundaria.
                      // PF ou PJ sem fantasia -> mostra c.name padrao.
                      const isPJWithFantasy = c.doc_type === 'cnpj' && c.trade_name && c.trade_name.trim();
                      const primary = isPJWithFantasy ? c.trade_name : c.name;
                      const secondary = isPJWithFantasy ? (c.legal_name || c.name) : null;
                      return (
                      <button
                        type="button"
                        key={c.id}
                        onMouseDown={(e) => { e.preventDefault(); pickClient(c); }}
                        className={`w-full text-left px-3 py-2 hover:bg-violet-50 text-sm flex justify-between items-start gap-3 border-b border-slate-100 last:border-b-0 ${clientId === c.id ? 'bg-violet-50' : ''}`}
                        data-testid={`invoice-client-opt-${c.id}`}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="font-bold text-slate-800 block truncate">{primary}</span>
                          {secondary && secondary !== primary && (
                            <span className="text-[11px] text-sky-700 font-semibold block truncate">{secondary}</span>
                          )}
                          {c.document && <span className="text-xs text-slate-500 block">{c.document}</span>}
                        </span>
                        {c.phone && <span className="text-[10px] text-slate-400 flex-shrink-0 mt-0.5">{c.phone}</span>}
                      </button>
                      );
                    })}
                  </div>
                )}
                {showClientList && clientSearch && filteredClients.length === 0 && (
                  <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2 text-xs text-slate-500" data-testid="invoice-client-empty">
                    Nenhum cliente encontrado — clique no <strong>+</strong> ao lado para cadastrar.
                  </div>
                )}
              </div>
              <div>
                <label className={labelCls}>CPF / CNPJ {tipo === 'nfe' && <span className="text-rose-500">*</span>}</label>
                <input value={customerDoc} onChange={e => setCustomerDoc(e.target.value)} className={inputClass} placeholder={tipo === 'nfce' ? 'Opcional — CPF do consumidor' : 'CPF ou CNPJ do destinatario'} data-testid="invoice-customer-doc" />
              </div>
            </div>
            {/* iter77ar: Reminder when client is selected but cadastro is incomplete for NF-e */}
            {tipo === 'nfe' && clientId && (() => {
              const cli = clients.find(c => c.id === clientId);
              if (!cli) return null;
              const cepDigits = (cli.end_cep || '').replace(/\D/g, '');
              const codMunDigits = (cli.end_cod_municipio || '').replace(/\D/g, '');
              const missing = [];
              if (!cli.end_logradouro) missing.push('logradouro');
              if (!cli.end_bairro) missing.push('bairro');
              if (!cli.end_municipio) missing.push('municipio');
              if (!cli.end_uf) missing.push('UF');
              if (cepDigits.length !== 8) missing.push('CEP');
              if (codMunDigits.length !== 7) missing.push('codigo IBGE municipio');
              if (missing.length === 0) return null;
              return (
                <div className="bg-amber-50 border-l-4 border-amber-400 px-3 py-2 rounded-r flex items-start gap-2 text-xs text-amber-800" data-testid="invoice-client-incomplete-warning">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-amber-600" />
                  <div className="flex-1">
                    <p className="font-bold">Cadastro fiscal do cliente incompleto para NF-e</p>
                    <p>Falta: <strong>{missing.join(', ')}</strong>. Clique no <Edit size={10} className="inline" /> ao lado de &ldquo;Cliente&rdquo; para corrigir antes de emitir.</p>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* iter77bc: Tabs Produtos / Transporte (Transporte so para NF-e modelo 55) */}
          {/* iter156f: removido `overflow-hidden` — estava clippando o dropdown de autocomplete
              de produtos, deixando a segunda sugestao cortada abaixo do card. Bordas continuam
              arredondadas via rounded-xl. */}
          <div className="border border-slate-200 rounded-xl">
            <div className="border-b border-slate-200 bg-slate-50 flex rounded-t-xl" data-testid="products-tabs">
              <button
                type="button"
                onClick={() => setProductsTab('produtos')}
                className={`px-5 py-2.5 text-xs font-black uppercase tracking-wider flex items-center gap-1.5 transition-colors ${productsTab === 'produtos' ? 'bg-white text-violet-700 border-b-2 border-violet-600' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="products-tab-produtos"
              >
                <Package size={14} /> Produtos
                {items.length > 0 && <span className="ml-1 bg-violet-100 text-violet-700 text-[10px] px-1.5 py-0.5 rounded-full">{items.length}</span>}
              </button>
              {tipo === 'nfe' && (
                <button
                  type="button"
                  onClick={() => setProductsTab('transporte')}
                  className={`px-5 py-2.5 text-xs font-black uppercase tracking-wider flex items-center gap-1.5 transition-colors ${productsTab === 'transporte' ? 'bg-white text-violet-700 border-b-2 border-violet-600' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="products-tab-transporte"
                >
                  <Truck size={14} /> Transporte
                </button>
              )}
            </div>

            {/* TAB: Produtos */}
            {productsTab === 'produtos' && (
            <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-black text-slate-500 uppercase tracking-wider">Itens <span className="text-rose-500">*</span></p>
              <button type="button" onClick={addEmptyLine} className="text-xs font-bold text-violet-600 hover:text-violet-700 flex items-center gap-1" data-testid="invoice-add-line"><Plus size={12} /> Linha vazia</button>
            </div>

            {/* iter77ap: Entry row — usuario escolhe produto, ajusta qty/preco e SO ENTAO clica "+". */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5" data-testid="invoice-entry-row">
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-12 md:col-span-5 relative">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">Descricao</label>
                  <div className="relative">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={entryProduct ? entryProduct.name : searchProd}
                      onChange={e => { setSearchProd(e.target.value); setEntryProduct(null); }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveProdIdx(i => Math.min(i + 1, products.length - 1)); }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveProdIdx(i => Math.max(0, i - 1)); }
                        else if (e.key === 'Enter' && products[activeProdIdx]) {
                          e.preventDefault();
                          const p = products[activeProdIdx];
                          setEntryProduct(p); setEntryQty(1); setEntryPrice(Number(p.price) || 0);
                          setSearchProd(''); setProducts([]);
                        }
                      }}
                      placeholder="Busque o produto cadastrado..."
                      className={`${inputClass} pl-7 pr-9 text-xs`}
                      data-testid="invoice-search-prod"
                    />
                    {/* iter156ac: botao "+" para cadastrar produto na hora (fluxo espelho do "Cadastrar Cliente") */}
                    <button
                      type="button"
                      onClick={() => setShowQuickProduct(true)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md bg-violet-100 hover:bg-violet-200 text-violet-700 flex items-center justify-center transition-colors"
                      title="Cadastrar novo produto"
                      data-testid="invoice-quick-product-btn"
                    >
                      <Plus size={13} />
                    </button>
                  </div>
                  {products.length > 0 && !entryProduct && (
                    <div className="absolute z-40 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto" data-testid="invoice-search-results">
                      {products.map((p, i) => (
                        <button
                          type="button"
                          key={p.id}
                          onClick={() => { setEntryProduct(p); setEntryQty(1); setEntryPrice(Number(p.price) || 0); setSearchProd(''); setProducts([]); }}
                          onMouseEnter={() => setActiveProdIdx(i)}
                          className={`w-full text-left px-3 py-2 hover:bg-violet-50 text-xs flex justify-between items-center gap-3 ${i === activeProdIdx ? 'bg-violet-100' : ''}`}
                          data-testid={`invoice-prod-${p.id}`}
                        >
                          <span className="truncate">
                            {i === activeProdIdx && <span className="text-[9px] font-bold text-violet-600 mr-1">(Enter)</span>}
                            {p.name}
                            {p.fiscal?.ncm && <span className="text-[9px] text-slate-400 ml-2">NCM {p.fiscal.ncm}</span>}
                          </span>
                          <span className="text-xs text-slate-500 whitespace-nowrap">{fmtBRL(p.price)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-span-3 md:col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">Qtd</label>
                  <input type="number" step="0.01" min="0" value={entryQty} onChange={e => setEntryQty(e.target.value)} className={`${inputClass} text-right text-xs`} data-testid="invoice-entry-qty" />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">Vlr Unit</label>
                  <BRLInput value={entryPrice} onChange={(v) => setEntryPrice(v)} className={`${inputClass} text-right text-xs`} dataTestid="invoice-entry-price" />
                </div>
                <div className="col-span-3 md:col-span-2 text-right">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">Total</label>
                  <p className="px-2 py-2 text-xs font-black text-violet-700" data-testid="invoice-entry-total">{fmtBRL((Number(entryQty) || 0) * (Number(entryPrice) || 0))}</p>
                </div>
                <div className="col-span-2 md:col-span-1">
                  <button
                    type="button"
                    onClick={() => {
                      if (!entryProduct) { return; }
                      const f = entryProduct.fiscal || {};
                      setItems(prev => [...prev, {
                        product_id: entryProduct.id,
                        name: entryProduct.name,
                        sku: entryProduct.sku || '',
                        quantity: Number(entryQty) || 1,
                        unit_price: Number(entryPrice) || 0,
                        ncm: f.ncm || '',
                        cest: f.cest || '',
                        cfop: f.cfop_default || '',
                        origem: f.origem || '0',
                        unit_commercial: f.unit_commercial || '',
                        icms_cst: f.icms_cst || '',
                        icms_aliquota: f.icms_aliquota ?? null,
                        pis_cst: f.pis_cst || '',
                        cofins_cst: f.cofins_cst || '',
                      }]);
                      setEntryProduct(null); setEntryQty(1); setEntryPrice(0);
                    }}
                    disabled={!entryProduct || !(Number(entryQty) > 0)}
                    className="w-full h-9 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white flex items-center justify-center font-bold text-sm shadow"
                    data-testid="invoice-entry-add-btn"
                    title={entryProduct ? 'Adicionar item' : 'Selecione um produto primeiro'}
                  ><Plus size={16} /></button>
                </div>
              </div>
              {entryProduct && (
                <p className="text-[10px] text-slate-500 mt-1.5">
                  <span className="text-violet-600 font-bold">Pronto:</span> ajuste a quantidade/valor acima e clique <strong>+</strong> para adicionar.
                  {entryProduct.fiscal?.ncm && <span className="ml-2 text-slate-400">NCM do cadastro: {entryProduct.fiscal.ncm}</span>}
                </p>
              )}
            </div>

            {items.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Nenhum item ainda. Busque o produto acima, ajuste qtd/valor e clique no <strong>+</strong>.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] text-slate-500 uppercase">
                      <th className="text-left pb-1.5">Descricao</th>
                      <th className="text-left pb-1.5 w-24">NCM <span className="text-rose-500">*</span></th>
                      <th className="text-right pb-1.5 w-20">Qtd</th>
                      <th className="text-right pb-1.5 w-28">Vlr Unit</th>
                      <th className="text-right pb-1.5 w-28">Total</th>
                      <th className="w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((it, idx) => {
                      const ncmDigits = (it.ncm || '').replace(/\D/g, '');
                      const ncmBad = (tipo === 'nfe' || tipo === 'nfce') && ncmDigits.length !== 8;
                      const cstBad = (tipo === 'nfe' || tipo === 'nfce') && !it.icms_cst;
                      return (
                        <tr key={idx}>
                          <td className="py-1.5 pr-2">
                            <input value={it.name} onChange={e => updItem(idx, 'name', e.target.value)} className={`${inputClass} text-xs`} data-testid={`invoice-item-name-${idx}`} placeholder="Descricao" />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              value={it.ncm || ''}
                              onChange={e => updItem(idx, 'ncm', e.target.value.replace(/\D/g, '').slice(0, 8))}
                              className={`${inputClass} text-xs font-mono ${ncmBad ? 'border-rose-400 bg-rose-50' : ''}`}
                              placeholder="8 digitos"
                              maxLength={8}
                              data-testid={`invoice-item-ncm-${idx}`}
                              title={ncmBad ? 'NCM obrigatorio — 8 digitos' : ''}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input type="number" step="0.01" value={it.quantity} onChange={e => updItem(idx, 'quantity', e.target.value)} className={`${inputClass} text-right text-xs`} data-testid={`invoice-item-qty-${idx}`} />
                          </td>
                          <td className="py-1.5 pr-2">
                            <BRLInput value={it.unit_price} onChange={(v) => updItem(idx, 'unit_price', v)} className={`${inputClass} text-right text-xs`} dataTestid={`invoice-item-price-${idx}`} />
                          </td>
                          <td className="py-1.5 pr-2 text-right font-bold text-slate-800 text-xs">{fmtBRL((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</td>
                          <td className="py-1.5">
                            <div className="flex items-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => setFiscalEditIdx(idx)}
                                title={cstBad ? 'CST/CSOSN sera herdado da natureza — clique para definir e ver ICMS-ST/PIS/COFINS/IPI' : 'Tributação deste item (CST, CFOP, ICMS-ST, PIS, COFINS, IPI)'}
                                className={`p-1.5 rounded ${cstBad ? 'text-amber-500 hover:bg-amber-50' : 'text-violet-500 hover:bg-violet-50'}`}
                                data-testid={`invoice-item-fiscal-${idx}`}
                              ><Settings size={12} /></button>
                              <button type="button" onClick={() => rmItem(idx)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" data-testid={`invoice-item-remove-${idx}`}><Trash2 size={12} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            </div>
            )}{/* fim TAB Produtos */}

            {/* TAB Transporte (apenas NF-e — modelo 55) */}
            {productsTab === 'transporte' && tipo === 'nfe' && (
              <div className="p-4 space-y-3" data-testid="invoice-transporte-section">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-black text-slate-500 uppercase tracking-wider">Dados de Transporte</p>
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={showTransporte} onChange={e => setShowTransporte(e.target.checked)} className="w-4 h-4 accent-violet-600" data-testid="invoice-transporte-toggle" />
                    Informar dados de transporte
                  </label>
                </div>
                {showTransporte && (
                  <>
                    <div>
                      <label className={labelCls}>Modalidade do Frete</label>
                      <select value={transporte.modFrete} onChange={e => setTransporte({ ...transporte, modFrete: e.target.value })} className={inputClass} data-testid="invoice-transp-modfrete">
                        <option value="0">0 - Contratacao do Frete por conta do Remetente (CIF)</option>
                        <option value="1">1 - Contratacao do Frete por conta do Destinatario (FOB)</option>
                        <option value="2">2 - Contratacao do Frete por conta de Terceiros</option>
                        <option value="3">3 - Transporte Proprio por conta do Remetente</option>
                        <option value="4">4 - Transporte Proprio por conta do Destinatario</option>
                        <option value="9">9 - Sem Ocorrencia de Transporte</option>
                      </select>
                      {/* iter156dt7: aviso educacional das regras SEFAZ p/ evitar cStat 868/869 */}
                      {(() => {
                        const mf = String(transporte.modFrete || '9');
                        if (mf === '9') return <p className="text-[10px] text-slate-500 mt-1">Nenhum dado de transporte sera enviado (transportadora, veiculo e volumes serao ignorados).</p>;
                        if (mf === '0' || mf === '1' || mf === '2') return <p className="text-[10px] text-amber-700 mt-1"><strong>SEFAZ:</strong> nesta modalidade, so pode informar <strong>Transportadora terceira</strong>. Dados do <strong>Veiculo</strong> (placa/UF/ANTT) sao ignorados automaticamente para evitar rejeicao.</p>;
                        if (mf === '3' || mf === '4') return <p className="text-[10px] text-amber-700 mt-1"><strong>SEFAZ:</strong> transporte proprio — nao informar transportadora terceira. Somente <strong>dados do veiculo</strong> (placa/UF/ANTT) sao aceitos. Terceiros informados serao ignorados.</p>;
                        return null;
                      })()}
                    </div>

                    {/* iter77ct.57+.58: campo unificado — o proprio input de Razao Social eh AUTOCOMPLETE:
                        digita e aparecem sugestoes de transportadoras cadastradas (fornecedores com is_transportadora=true).
                        Ao lado, botao "+" abre modal rapido de cadastro. Nao ha campo separado — tudo integrado. */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2 relative">
                        <label className={labelCls}>Transportadora (Razao Social) <span className="text-[10px] text-violet-600 font-normal">— digite para buscar cadastrada ou + para nova</span></label>
                        <div className="flex gap-1">
                          <div className="flex-1 relative">
                            <input
                              value={transporte.transp_xnome}
                              onChange={e => { setTransporte({ ...transporte, transp_xnome: e.target.value }); setSelectedTransportadoraId(''); }}
                              onFocus={() => setShowTranspSuggestions(true)}
                              onBlur={() => setTimeout(() => setShowTranspSuggestions(false), 150)}
                              className={inputClass}
                              placeholder="Transportadora Brasil Ltda"
                              data-testid="invoice-transp-nome"
                            />
                            {/* iter77ct.58: dropdown de sugestoes — mostra ao focar OU ao digitar. Filtra por nome/CNPJ. */}
                            {showTranspSuggestions && (() => {
                              const q = (transporte.transp_xnome || '').trim().toLowerCase();
                              const filtered = transportadoras.filter(t => {
                                if (!q) return true;
                                return (t.name || '').toLowerCase().includes(q) || (t.document || '').includes(q);
                              }).slice(0, 8);
                              if (filtered.length === 0) return null;
                              return (
                                <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-violet-200 rounded-lg shadow-xl max-h-64 overflow-y-auto" data-testid="invoice-transp-suggestions">
                                  {filtered.map(t => (
                                    <button
                                      key={t.id}
                                      type="button"
                                      onMouseDown={e => { e.preventDefault(); applyTransportadora(t.id); setShowTranspSuggestions(false); }}
                                      className="w-full text-left px-3 py-2 hover:bg-violet-50 border-b border-slate-100 last:border-0"
                                      data-testid={`invoice-transp-sug-${t.id}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <Truck size={11} className="text-violet-500 flex-shrink-0" />
                                        <span className="text-xs font-semibold text-slate-800 truncate flex-1">{t.name}</span>
                                        {t.document && <span className="text-[10px] text-slate-400 font-mono">{t.document}</span>}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowNewTransportadora(true)}
                            className="px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 flex-shrink-0"
                            title="Cadastrar nova transportadora (vai pro cadastro de Fornecedores)"
                            data-testid="invoice-transp-add-new"
                          >
                            <Plus size={13} />
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>CNPJ ou CPF</label>
                        <input value={transporte.transp_cnpj || transporte.transp_cpf} onChange={e => {
                          const v = e.target.value;
                          const digits = v.replace(/\D/g, '');
                          setTransporte({ ...transporte, transp_cnpj: digits.length > 11 ? digits : '', transp_cpf: digits.length <= 11 ? digits : '' });
                        }} className={inputClass} data-testid="invoice-transp-doc" />
                      </div>
                      <div>
                        <label className={labelCls}>IE</label>
                        <input value={transporte.transp_ie} onChange={e => setTransporte({ ...transporte, transp_ie: e.target.value })} className={inputClass} data-testid="invoice-transp-ie" />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelCls}>Endereco</label>
                        <input value={transporte.transp_xender} onChange={e => setTransporte({ ...transporte, transp_xender: e.target.value })} className={inputClass} data-testid="invoice-transp-end" />
                      </div>
                      <div>
                        <label className={labelCls}>Municipio</label>
                        <input value={transporte.transp_xmun} onChange={e => setTransporte({ ...transporte, transp_xmun: e.target.value })} className={inputClass} data-testid="invoice-transp-mun" />
                      </div>
                      <div>
                        <label className={labelCls}>UF</label>
                        <input value={transporte.transp_uf} onChange={e => setTransporte({ ...transporte, transp_uf: e.target.value.toUpperCase().slice(0, 2) })} className={inputClass} maxLength={2} data-testid="invoice-transp-uf" />
                      </div>
                      <div>
                        <label className={labelCls}>Codigo ANTT (RNTC)</label>
                        <input value={transporte.transp_antt} onChange={e => setTransporte({ ...transporte, transp_antt: e.target.value })} className={inputClass} data-testid="invoice-transp-antt" />
                      </div>
                    </div>

                    <p className="text-[11px] font-bold text-slate-400 uppercase pt-2">Veiculo</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className={labelCls}>Placa</label>
                        <input value={transporte.veic_placa} onChange={e => setTransporte({ ...transporte, veic_placa: e.target.value.toUpperCase() })} className={inputClass} placeholder="ABC1234" maxLength={7} data-testid="invoice-veic-placa" />
                      </div>
                      <div>
                        <label className={labelCls}>UF do Veiculo</label>
                        <input value={transporte.veic_uf} onChange={e => setTransporte({ ...transporte, veic_uf: e.target.value.toUpperCase().slice(0, 2) })} className={inputClass} maxLength={2} data-testid="invoice-veic-uf" />
                      </div>
                    </div>

                    <p className="text-[11px] font-bold text-slate-400 uppercase pt-2">Volumes</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className={labelCls}>Quantidade</label>
                        <input type="number" min="0" step="1" value={transporte.vol_qtd} onChange={e => setTransporte({ ...transporte, vol_qtd: e.target.value })} className={inputClass} data-testid="invoice-vol-qtd" />
                      </div>
                      <div>
                        <label className={labelCls}>Especie</label>
                        <input value={transporte.vol_especie} onChange={e => setTransporte({ ...transporte, vol_especie: e.target.value })} className={inputClass} placeholder="CAIXA, PALLET..." data-testid="invoice-vol-especie" />
                      </div>
                      <div>
                        <label className={labelCls}>Marca</label>
                        <input value={transporte.vol_marca} onChange={e => setTransporte({ ...transporte, vol_marca: e.target.value })} className={inputClass} data-testid="invoice-vol-marca" />
                      </div>
                      <div>
                        <label className={labelCls}>Numeracao</label>
                        <input value={transporte.vol_num} onChange={e => setTransporte({ ...transporte, vol_num: e.target.value })} className={inputClass} data-testid="invoice-vol-num" />
                      </div>
                      <div>
                        <label className={labelCls}>Peso Bruto (kg)</label>
                        <input type="number" min="0" step="0.001" value={transporte.vol_peso_b} onChange={e => setTransporte({ ...transporte, vol_peso_b: e.target.value })} className={inputClass} data-testid="invoice-vol-peso-b" />
                      </div>
                      <div>
                        <label className={labelCls}>Peso Liquido (kg)</label>
                        <input type="number" min="0" step="0.001" value={transporte.vol_peso_l} onChange={e => setTransporte({ ...transporte, vol_peso_l: e.target.value })} className={inputClass} data-testid="invoice-vol-peso-l" />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>{/* fim do container de tabs Produtos/Transporte */}

          {/* iter77bc: Pagamento modernizado — form "Adicionar" + tabela de formas adicionadas + Frete */}
          <div className="border border-slate-200 rounded-xl p-4 space-y-4" data-testid="invoice-payment-section">
            <div className="flex items-center justify-between">
              <p className="text-xs font-black text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <Receipt size={14} className="text-violet-600" />
                Pagamento
              </p>
              <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-lg px-3 py-1.5">
                <span className="text-[10px] font-bold text-violet-600 uppercase mr-2">Total NF</span>
                <span className="text-base font-black text-violet-800" data-testid="invoice-total">{fmtBRL(total)}</span>
                <span className="text-[10px] text-slate-500 ml-2">Subtotal: {fmtBRL(subtotal)}</span>
              </div>
            </div>

            {/* iter156co: Resumo de tributos aplicados (Natureza + overrides por item) */}
            <TaxSummaryRow items={items} tipo={tipo} natop={selectedNatop} companyId={companyId}
              previewPayload={{ tipo, company_id: companyId, natureza_op_id: naturezaId || null, client_id: clientId || null, customer_name: customerName, customer_doc: customerDoc, items, discount: Number(discount) || 0, surcharge: Number(surcharge) || 0, freight: Number(freight) || 0, payment_method: (paymentLines[0]?.method) || 'dinheiro', finalidade: finalidade || null, transporte: showTransporte ? transporte : null }} />

            {/* Linha "Adicionar forma" */}
            <PaymentAddRow
              paymentMethods={paymentMethods}
              suggestedValue={Math.max(0, total - paymentLines.reduce((s, p) => s + (Number(p.value) || 0), 0))}
              suggestedDate={dueDate || _todayISO}
              onAdd={(line) => setPaymentLines(arr => [...arr, line])}
              inputClass={inputClass}
              labelCls={labelCls}
            />

            {/* Tabela de formas adicionadas */}
            {paymentLines.length > 0 ? (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="invoice-payment-lines">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left w-10">#</th>
                      <th className="px-3 py-2 text-left">Forma de Pagamento</th>
                      <th className="px-3 py-2 text-left w-44">Data</th>
                      <th className="px-3 py-2 text-right w-32">Valor</th>
                      <th className="px-3 py-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paymentLines.map((line, i) => {
                      const pm = paymentMethods.find(p => (p.type || p.name) === line.method);
                      return (
                        <tr key={i} data-testid={`payment-line-${i}`} className="hover:bg-slate-50/60">
                          <td className="px-3 py-2 text-slate-500 text-xs">{i + 1}</td>
                          <td className="px-3 py-2 font-bold text-slate-700">{pm?.name || line.method || '—'}</td>
                          <td className="px-3 py-2">
                            <input
                              type="date"
                              value={line.due_date || ''}
                              onChange={e => setPaymentLines(arr => arr.map((p, j) => j === i ? { ...p, due_date: e.target.value } : p))}
                              className={`${inputClass} text-xs`}
                              data-testid={`payment-due-${i}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <BRLInput
                              value={line.value}
                              onChange={(v) => setPaymentLines(arr => arr.map((p, j) => j === i ? { ...p, value: v } : p))}
                              className={`${inputClass} text-right text-xs`}
                              dataTestid={`payment-value-${i}`}
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => setPaymentLines(arr => arr.filter((_, j) => j !== i))}
                              className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"
                              title="Remover"
                              data-testid={`payment-remove-${i}`}
                            ><Trash2 size={14} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50 text-xs">
                    <tr>
                      <td colSpan={2} className="px-3 py-2 font-bold text-slate-600">Total de Itens: {paymentLines.length}</td>
                      <td className="px-3 py-2 text-right text-slate-500">Total Recebido:</td>
                      <td className="px-3 py-2 text-right font-black text-emerald-700" data-testid="invoice-total-recebido">
                        {fmtBRL(paymentLines.reduce((s, p) => s + (Number(p.value) || 0), 0))}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic py-2">Nenhuma forma adicionada. Selecione a forma + valor + vencimento acima e clique Adicionar.</p>
            )}

            {/* Toggle Desconto/Acrescimo/Frete */}
            <div className="pt-1 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowAdjustments(s => !s)}
                className="text-xs font-bold text-violet-600 hover:text-violet-700 flex items-center gap-1"
                data-testid="invoice-toggle-adjustments"
              >
                {showAdjustments ? '−' : '+'} {showAdjustments ? 'Ocultar ajustes' : 'Adicionar Desconto / Acrescimo / Frete'}
              </button>
              {showAdjustments && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2" data-testid="invoice-adjustments">
                  <div>
                    <label className={labelCls}>Desconto (R$)</label>
                    <BRLInput value={discount} onChange={(v) => setDiscount(v)} className={inputClass} dataTestid="invoice-discount" />
                  </div>
                  <div>
                    <label className={labelCls}>Acrescimo (R$)</label>
                    <BRLInput value={surcharge} onChange={(v) => setSurcharge(v)} className={inputClass} dataTestid="invoice-surcharge" />
                  </div>
                  <div>
                    <label className={labelCls}>Frete (R$)</label>
                    <BRLInput value={freight} onChange={(v) => setFreight(v)} className={inputClass} dataTestid="invoice-freight" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* iter77bc: bloco antigo de Transporte removido — agora vive na aba Transporte dentro do container Produtos/Transporte */}

          <div>
            <label className={labelCls}>Observacoes / Informacoes Complementares</label>
            <textarea value={notes} onChange={e => { setNotes(e.target.value); setNotesTouched(true); }} rows={2} className={inputClass} data-testid="invoice-notes" placeholder="Auto-preenchido da Natureza de Operacao. Edite para sobrescrever nesta nota." maxLength={5000} />
            <p className="text-[10px] text-slate-400 mt-1">Este texto aparece no DANFE (tag infCpl do XML). A SEFAZ nao aceita quebras de linha: elas sao convertidas em espaco automaticamente. {selectedNatop?.info_complementares && !notesTouched ? <span className="text-emerald-600">Sincronizado com a NatOp.</span> : null}</p>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex items-center justify-between gap-3">
          <p className="text-[10px] text-slate-400">
            {hasBlockingIssues
              ? <span className="text-rose-600 font-bold">Preencha os campos obrigatorios marcados com <span className="text-rose-500 font-black">*</span> para habilitar Salvar.</span>
              : <>Sera salvo como <span className="font-bold">rascunho</span>. A transmissao SEFAZ acontece em seguida.</>}
          </p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-800 text-sm font-bold" data-testid="invoice-cancel">Cancelar</button>
            <button type="button" onClick={handleSubmit} disabled={saving || hasBlockingIssues} className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm shadow-md flex items-center gap-2" data-testid="invoice-save">
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Save size={14} />}
              {isEdit ? 'Salvar Alteracoes' : 'Salvar Rascunho'}
            </button>
          </div>
        </div>

        {/* iter77ak: Fiscal quick-edit modal */}
        {fiscalEditIdx !== null && items[fiscalEditIdx] && (
          <FiscalQuickEditModal
            item={items[fiscalEditIdx]}
            onClose={() => setFiscalEditIdx(null)}
            onSave={(patch, updateMaster) => {
              setItems(prev => {
                const copy = [...prev];
                copy[fiscalEditIdx] = { ...copy[fiscalEditIdx], ...patch };
                return copy;
              });
              if (updateMaster && items[fiscalEditIdx].product_id) {
                axios.patch(`${API}/products/${items[fiscalEditIdx].product_id}/fiscal`, { fiscal: {
                  ncm: patch.ncm,
                  cest: patch.cest,
                  cfop_default: patch.cfop,
                  origem: patch.origem,
                  unit_commercial: patch.unit_commercial,
                  icms_cst: patch.icms_cst,
                  icms_aliquota: patch.icms_aliquota,
                  // iter156co: ST + demais tributos salvos no cadastro do produto
                  icms_aliquota_st: patch.icms_aliquota_st,
                  icms_mva: patch.icms_mva,
                  icms_red_bc: patch.icms_red_bc,
                  pis_cst: patch.pis_cst,
                  pis_aliquota: patch.pis_aliquota,
                  cofins_cst: patch.cofins_cst,
                  cofins_aliquota: patch.cofins_aliquota,
                  ipi_cst: patch.ipi_cst,
                  ipi_aliquota: patch.ipi_aliquota,
                } }, { withCredentials: true })
                  .then(() => toast.success('Cadastro do produto atualizado'))
                  .catch(err => toast.error(err.response?.data?.detail || 'Erro ao atualizar cadastro'));
              }
              setFiscalEditIdx(null);
            }}
          />
        )}

        {/* iter77aq+ar: Quick create/edit client modal */}
        {showQuickClient && (
          <QuickClientModal
            mode={showQuickClient.mode || 'create'}
            client={showQuickClient.client || null}
            onClose={() => setShowQuickClient(null)}
            onSaved={async (saved) => {
              await refreshClients();
              setClientId(saved.id);
              setCustomerName(saved.name || '');
              setCustomerDoc(saved.document || '');
              setCustomerEmail(saved.email || '');
              setClientSearch(saved.name || '');
              setShowQuickClient(null);
            }}
          />
        )}

        {/* iter156ac: Quick create product modal — cadastro rapido de produto direto na emissao */}
        {showQuickProduct && (
          <QuickProductModal
            onClose={() => setShowQuickProduct(false)}
            onSaved={async (created) => {
              // Seleciona automaticamente o produto recem cadastrado como entryProduct
              setEntryProduct(created);
              setEntryQty(1);
              setEntryPrice(Number(created.price) || 0);
              setSearchProd('');
              setProducts([]);
              setShowQuickProduct(false);
              toast.success(`Produto "${created.name}" cadastrado com sucesso.`);
            }}
          />
        )}

        {/* iter77ct.57: modal rapido para cadastrar transportadora (fornecedor com is_transportadora=true) */}
        {showNewTransportadora && (
          <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4">
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden" data-testid="new-transportadora-modal">
              <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-3 flex items-center justify-between">
                <h3 className="text-white font-bold text-sm flex items-center gap-2"><Truck size={16} /> Cadastrar Nova Transportadora</h3>
                <button type="button" onClick={() => setShowNewTransportadora(false)} className="text-white/80 hover:text-white"><X size={18} /></button>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className={labelCls}>Razao Social <span className="text-rose-500">*</span></label>
                    <input value={newTranspForm.name} onChange={e => setNewTranspForm({ ...newTranspForm, name: e.target.value })} className={inputClass} placeholder="Transportadora XYZ Ltda" data-testid="new-transp-name" />
                  </div>
                  <div>
                    <label className={labelCls}>CNPJ <span className="text-[10px] text-violet-600 font-bold">(autopreenche)</span></label>
                    <div className="flex gap-1">
                      <input
                        value={newTranspForm.document}
                        onChange={e => setNewTranspForm({ ...newTranspForm, document: e.target.value })}
                        onBlur={() => { if ((newTranspForm.document || '').replace(/\D/g, '').length === 14 && !newTranspForm.name) lookupTranspCnpj(); }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupTranspCnpj(); } }}
                        className={`${inputClass} flex-1`}
                        placeholder="00.000.000/0000-00"
                        data-testid="new-transp-cnpj"
                      />
                      <button
                        type="button"
                        onClick={lookupTranspCnpj}
                        disabled={newTranspLookingUp || (newTranspForm.document || '').replace(/\D/g, '').length !== 14}
                        className="px-3 py-2 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 disabled:from-slate-300 disabled:to-slate-400 text-white text-xs font-bold shadow-md flex items-center justify-center flex-shrink-0"
                        title="Buscar dados na Receita Federal"
                        data-testid="new-transp-cnpj-lookup"
                      >
                        {newTranspLookingUp ? <RefreshCw size={13} className="animate-spin" /> : <Search size={13} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Inscricao Estadual</label>
                    <input value={newTranspForm.transp_ie} onChange={e => setNewTranspForm({ ...newTranspForm, transp_ie: e.target.value })} className={inputClass} placeholder="Somente numeros" data-testid="new-transp-ie" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Endereco</label>
                    <input value={newTranspForm.transp_endereco} onChange={e => setNewTranspForm({ ...newTranspForm, transp_endereco: e.target.value })} className={inputClass} placeholder="Rua, numero, bairro..." data-testid="new-transp-end" />
                  </div>
                  <div>
                    <label className={labelCls}>Municipio</label>
                    <input value={newTranspForm.transp_municipio} onChange={e => setNewTranspForm({ ...newTranspForm, transp_municipio: e.target.value })} className={inputClass} placeholder="Ex.: Goiania" data-testid="new-transp-mun" />
                  </div>
                  <div>
                    <label className={labelCls}>UF</label>
                    <input value={newTranspForm.transp_uf} onChange={e => setNewTranspForm({ ...newTranspForm, transp_uf: e.target.value.toUpperCase().slice(0, 2) })} className={inputClass} maxLength={2} placeholder="GO" data-testid="new-transp-uf" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>RNTC / ANTT (opcional)</label>
                    <input value={newTranspForm.transp_antt} onChange={e => setNewTranspForm({ ...newTranspForm, transp_antt: e.target.value })} className={inputClass} placeholder="Registro ANTT" data-testid="new-transp-antt" />
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-2">Sera criado como Fornecedor com a flag "Transportadora" marcada. Voce pode edita-lo depois em Cadastros &gt; Fornecedores.</p>
              </div>
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                <button type="button" onClick={() => setShowNewTransportadora(false)} disabled={creatingTransp} className="px-4 py-2 text-slate-600 text-xs font-semibold hover:bg-slate-100 rounded-lg">Cancelar</button>
                <button type="button" onClick={createTransportadora} disabled={creatingTransp || !newTranspForm.name.trim()} className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white text-xs font-bold rounded-lg flex items-center gap-1.5" data-testid="new-transp-save">
                  {creatingTransp ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />} {creatingTransp ? 'Salvando...' : 'Cadastrar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


/* ========================= QuickProductModal — iter156ac ========================= */
/* Cadastro rapido de produto direto do modal de emissao NF-e (fluxo espelho do
   QuickClientModal). Campos minimos essenciais: nome, SKU, preco, custo, unidade
   comercial, NCM e CFOP padrao. A configuracao fiscal completa (CEST, aliquotas etc.)
   continua sendo feita na tela de Produtos > Fiscal. */
const QuickProductModal = ({ onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [price, setPrice] = useState(0);
  const [cost, setCost] = useState(0);
  const [unit, setUnit] = useState('UN');
  const [ncm, setNcm] = useState('');
  const [cfop, setCfop] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Informe o nome do produto'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        sku: sku.trim(),
        price: Number(price) || 0,
        cost: Number(cost) || 0,
        active: true,
        fiscal: {
          unit_commercial: unit || 'UN',
          ncm: (ncm || '').replace(/\D/g, ''),
          cfop_default: (cfop || '').replace(/\D/g, ''),
        },
      };
      const { data } = await axios.post(`${API}/products`, payload, { withCredentials: true });
      // Backend retorna produto criado. Precisamos mapear para o formato usado no
      // dropdown de busca (com `fiscal` embutido) — o retorno ja traz.
      onSaved({
        id: data.id || data._id,
        name: data.name || name,
        sku: data.sku || sku,
        price: data.price ?? price,
        fiscal: data.fiscal || payload.fiscal,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao cadastrar produto');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4" data-testid="quick-product-overlay">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden ring-1 ring-slate-200" onClick={e => e.stopPropagation()} data-testid="quick-product-modal">
        <div className="px-5 pt-4 pb-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package size={18} />
            <p className="text-base font-black">Cadastrar Produto</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5" data-testid="quick-product-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Nome *</label>
            <input value={name} onChange={e => setName(e.target.value)} className={inputClass} placeholder="Ex.: Cerveja Skol 350ml" data-testid="quick-product-name" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">SKU</label>
              <input value={sku} onChange={e => setSku(e.target.value)} className={inputClass} placeholder="Codigo interno" data-testid="quick-product-sku" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Unidade</label>
              <input value={unit} onChange={e => setUnit(e.target.value.toUpperCase())} className={inputClass} placeholder="UN / KG / L" maxLength={6} data-testid="quick-product-unit" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Preco (R$) *</label>
              <BRLInput value={price} onChange={setPrice} className={inputClass} dataTestid="quick-product-price" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Custo (R$)</label>
              <BRLInput value={cost} onChange={setCost} className={inputClass} dataTestid="quick-product-cost" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">NCM</label>
              <input value={ncm} onChange={e => setNcm(e.target.value.replace(/\D/g, '').slice(0, 8))} className={inputClass} placeholder="8 digitos" inputMode="numeric" data-testid="quick-product-ncm" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">CFOP Padrao</label>
              <input value={cfop} onChange={e => setCfop(e.target.value.replace(/\D/g, '').slice(0, 4))} className={inputClass} placeholder="Ex.: 5102" inputMode="numeric" data-testid="quick-product-cfop" />
            </div>
          </div>
          <p className="text-[10px] text-slate-400 italic pt-1">
            Cadastro rapido — CEST, aliquotas e demais dados fiscais completos podem ser preenchidos em <strong>Produtos &gt; Fiscal</strong> depois.
          </p>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-bold" data-testid="quick-product-cancel">Cancelar</button>
            <button onClick={handleSave} disabled={saving || !name.trim()} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white text-sm font-bold flex items-center gap-2" data-testid="quick-product-save">
              {saving ? <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" /> : <Check size={14} />}
              <span>{saving ? 'Salvando...' : 'Cadastrar'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ========================= QuickClientModal — iter77aq + iter77ar ========================= */
const QuickClientModal = ({ mode = 'create', client = null, onClose, onSaved }) => {
  const isEdit = mode === 'edit' && client?.id;
  const [name, setName] = useState(client?.name || '');
  const [docType, setDocType] = useState(client?.doc_type || (((client?.document || '').replace(/\D/g, '').length > 11) ? 'cnpj' : 'cpf'));
  const [document, setDocumentVal] = useState(client?.document || '');
  const [phone, setPhone] = useState(client?.phone || '');
  const [email, setEmail] = useState(client?.email || '');
  // iter77ar: fiscal address fields — exigidos pra NF-e (cStat 225 quando faltam).
  const [endCep, setEndCep] = useState(client?.end_cep || '');
  const [endLogradouro, setEndLogradouro] = useState(client?.end_logradouro || '');
  const [endNumero, setEndNumero] = useState(client?.end_numero || '');
  const [endComplemento, setEndComplemento] = useState(client?.end_complemento || '');
  const [endBairro, setEndBairro] = useState(client?.end_bairro || '');
  const [endMunicipio, setEndMunicipio] = useState(client?.end_municipio || '');
  const [endUf, setEndUf] = useState(client?.end_uf || '');
  const [endCodMunicipio, setEndCodMunicipio] = useState(client?.end_cod_municipio || '');
  const [ieIndicator, setIeIndicator] = useState(client?.ie_indicator || '9');
  const [ie, setIe] = useState(client?.ie || '');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basicos');
  // iter77ct.55: lookup CEP via BrasilAPI — auto-preenche endereco fiscal + IBGE
  const [cepLookingUp, setCepLookingUp] = useState(false);
  // iter77ct.59: lookup CNPJ (BrasilAPI) — auto-preenche razao/nome/telefone/email/endereco/IE.
  // Para CPF, o backend nao tem lookup — mostra info explicando.
  const [docLookingUp, setDocLookingUp] = useState(false);
  const lookupDocument = async () => {
    const digits = (document || '').replace(/\D/g, '');
    if (digits.length !== 14) {
      toast.info(digits.length === 11 ? 'Busca automatica so funciona para CNPJ' : 'Digite um CNPJ com 14 digitos');
      return;
    }
    setDocLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/lookup/cnpj/${digits}`, { withCredentials: true });
      // Preenche apenas campos ainda vazios (nao sobrescreve o que usuario digitou)
      if (!name && (data.razao_social || data.nome_fantasia)) setName(data.razao_social || data.nome_fantasia);
      setDocumentVal(data.cnpj || digits);
      if (!phone && data.telefone) setPhone(data.telefone);
      if (!email && data.email) setEmail(data.email);
      if (data.logradouro && !endLogradouro) setEndLogradouro(data.logradouro);
      if (data.numero && !endNumero) setEndNumero(data.numero);
      if (data.complemento && !endComplemento) setEndComplemento(data.complemento);
      if (data.bairro && !endBairro) setEndBairro(data.bairro);
      if (data.municipio) setEndMunicipio(data.municipio);
      if (data.uf) setEndUf(data.uf);
      if (data.cep && !endCep) setEndCep((data.cep || '').replace(/\D/g, ''));
      if (data.codigo_municipio_ibge) setEndCodMunicipio(data.codigo_municipio_ibge);
      toast.success(`Dados de ${data.razao_social || 'empresa'} carregados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao consultar CNPJ');
    } finally {
      setDocLookingUp(false);
    }
  };
  const lookupCep = async () => {
    const digits = (endCep || '').replace(/\D/g, '');
    if (digits.length !== 8) { toast.info('Digite um CEP com 8 digitos'); return; }
    setCepLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/lookup/cep/${digits}`, { withCredentials: true });
      setEndCep(data.cep || digits);
      if (!endLogradouro && data.logradouro) setEndLogradouro(data.logradouro);
      if (!endBairro && data.bairro) setEndBairro(data.bairro);
      if (data.municipio) setEndMunicipio(data.municipio);
      if (data.uf) setEndUf(data.uf);
      if (data.codigo_municipio_ibge) setEndCodMunicipio(data.codigo_municipio_ibge);
      toast.success('Endereco preenchido');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao consultar CEP');
    } finally {
      setCepLookingUp(false);
    }
  };

  const submit = async () => {
    if (!name.trim()) { toast.error('Nome obrigatorio'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        doc_type: docType,
        document: document.trim(),
        phone: phone.trim(),
        email: email.trim(),
        person_type: docType === 'cnpj' ? 'juridica' : 'fisica',
        end_cep: (endCep || '').replace(/\D/g, ''),
        end_logradouro: endLogradouro.trim(),
        end_numero: endNumero.trim(),
        end_complemento: endComplemento.trim(),
        end_bairro: endBairro.trim(),
        end_municipio: endMunicipio.trim(),
        end_uf: endUf.toUpperCase().slice(0, 2),
        end_cod_municipio: (endCodMunicipio || '').replace(/\D/g, ''),
        ie_indicator: ieIndicator,
        ie: ie.trim(),
      };
      const url = isEdit ? `${ERP}/clients/${client.id}` : `${ERP}/clients`;
      const method = isEdit ? 'put' : 'post';
      const { data } = await axios[method](url, payload, { withCredentials: true });
      toast.success(isEdit ? 'Cliente atualizado' : 'Cliente cadastrado');
      // For PUT, backend may not return the full doc — merge.
      const saved = data && data.id ? data : { ...payload, id: client?.id };
      onSaved(saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="quick-client-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
        <div className="bg-gradient-to-r from-violet-600 to-fuchsia-500 px-5 py-3 text-white flex items-center justify-between">
          <p className="text-base font-black">{isEdit ? 'Editar Cliente' : 'Cadastrar Cliente'}</p>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded"><X size={18} /></button>
        </div>
        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-3">
          <button type="button" onClick={() => setTab('basicos')} className={`px-3 py-2 text-xs font-bold ${tab === 'basicos' ? 'text-violet-700 border-b-2 border-violet-600' : 'text-slate-500'}`} data-testid="qc-tab-basicos">Dados Basicos</button>
          <button type="button" onClick={() => setTab('fiscal')} className={`px-3 py-2 text-xs font-bold ${tab === 'fiscal' ? 'text-violet-700 border-b-2 border-violet-600' : 'text-slate-500'}`} data-testid="qc-tab-fiscal">Endereco Fiscal (NF-e)</button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          {tab === 'basicos' && (<>
            <div>
              <label className={labelCls}>Nome / Razao Social <span className="text-rose-500">*</span></label>
              <input value={name} onChange={e => setName(e.target.value)} className={inputClass} placeholder="Nome completo" autoFocus data-testid="quick-client-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Tipo</label>
                <select value={docType} onChange={e => setDocType(e.target.value)} className={inputClass} data-testid="quick-client-doctype">
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className={labelCls}>{docType === 'cnpj' ? 'CNPJ' : 'CPF'} {docType === 'cnpj' && <span className="text-[10px] text-violet-600 font-bold">(autopreenche via Receita)</span>}</label>
                <div className="flex gap-1">
                  <input
                    value={document}
                    onChange={e => setDocumentVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && docType === 'cnpj') { e.preventDefault(); lookupDocument(); } }}
                    onBlur={() => { if (docType === 'cnpj' && (document || '').replace(/\D/g, '').length === 14 && !name) { lookupDocument(); } }}
                    className={`${inputClass} flex-1`}
                    placeholder={docType === 'cnpj' ? '00.000.000/0000-00' : '000.000.000-00'}
                    data-testid="quick-client-doc"
                  />
                  {docType === 'cnpj' && (
                    <button
                      type="button"
                      onClick={lookupDocument}
                      disabled={docLookingUp || (document || '').replace(/\D/g, '').length !== 14}
                      className="px-3 py-2 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 disabled:from-slate-300 disabled:to-slate-400 text-white text-xs font-bold shadow-md flex items-center justify-center flex-shrink-0"
                      title="Buscar dados na Receita Federal"
                      data-testid="quick-client-cnpj-lookup"
                    >
                      {docLookingUp ? <RefreshCw size={13} className="animate-spin" /> : <Search size={13} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Telefone</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} className={inputClass} placeholder="(00) 00000-0000" data-testid="quick-client-phone" />
              </div>
              <div>
                <label className={labelCls}>E-mail</label>
                <input value={email} onChange={e => setEmail(e.target.value)} type="email" className={inputClass} placeholder="email@cliente.com" data-testid="quick-client-email" />
              </div>
            </div>
          </>)}
          {tab === 'fiscal' && (<>
            <p className="text-[11px] text-slate-500">Endereco fiscal eh OBRIGATORIO para emitir NF-e (modelo 55) ao cliente. Para NFC-e (cupom) eh opcional.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="col-span-2 md:col-span-1">
                <label className={labelCls}>CEP <span className="text-rose-500">*</span></label>
                <div className="flex gap-1">
                  <input
                    value={endCep}
                    onChange={e => setEndCep(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupCep(); } }}
                    className={`${inputClass} flex-1`}
                    maxLength={8}
                    placeholder="00000000"
                    data-testid="qc-end-cep"
                  />
                  <button
                    type="button"
                    onClick={lookupCep}
                    disabled={cepLookingUp || (endCep || '').replace(/\D/g, '').length !== 8}
                    title="Buscar endereco e IBGE do CEP"
                    className="px-2.5 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white rounded-lg text-xs font-bold flex items-center justify-center flex-shrink-0"
                    data-testid="qc-end-cep-lookup"
                  >
                    <Search size={13} className={cepLookingUp ? 'animate-pulse' : ''} />
                  </button>
                </div>
              </div>
              <div className="col-span-2 md:col-span-2">
                <label className={labelCls}>Logradouro <span className="text-rose-500">*</span></label>
                <input value={endLogradouro} onChange={e => setEndLogradouro(e.target.value)} className={inputClass} placeholder="Rua / Avenida" data-testid="qc-end-logradouro" />
              </div>
              <div>
                <label className={labelCls}>Numero</label>
                <input value={endNumero} onChange={e => setEndNumero(e.target.value)} className={inputClass} placeholder="SN" data-testid="qc-end-numero" />
              </div>
              <div className="col-span-2 md:col-span-2">
                <label className={labelCls}>Complemento</label>
                <input value={endComplemento} onChange={e => setEndComplemento(e.target.value)} className={inputClass} placeholder="Apto / Sala" data-testid="qc-end-complemento" />
              </div>
              <div className="col-span-2 md:col-span-2">
                <label className={labelCls}>Bairro <span className="text-rose-500">*</span></label>
                <input value={endBairro} onChange={e => setEndBairro(e.target.value)} className={inputClass} data-testid="qc-end-bairro" />
              </div>
              <div className="col-span-2 md:col-span-2">
                <label className={labelCls}>Municipio <span className="text-rose-500">*</span></label>
                <input value={endMunicipio} onChange={e => setEndMunicipio(e.target.value)} className={inputClass} data-testid="qc-end-municipio" />
              </div>
              <div>
                <label className={labelCls}>UF <span className="text-rose-500">*</span></label>
                <input value={endUf} onChange={e => setEndUf(e.target.value.toUpperCase().slice(0, 2))} className={inputClass} maxLength={2} placeholder="GO" data-testid="qc-end-uf" />
              </div>
              <div>
                <label className={labelCls}>Codigo IBGE <span className="text-rose-500">*</span></label>
                <input value={endCodMunicipio} onChange={e => setEndCodMunicipio(e.target.value.replace(/\D/g, '').slice(0, 7))} className={inputClass} maxLength={7} placeholder="7 digitos" data-testid="qc-end-cod-municipio" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Indicador de IE</label>
                <select value={ieIndicator} onChange={e => setIeIndicator(e.target.value)} className={inputClass} data-testid="qc-ie-indicator">
                  <option value="1">1 - Contribuinte ICMS</option>
                  <option value="2">2 - Contribuinte Isento</option>
                  <option value="9">9 - Nao Contribuinte (padrao)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Inscricao Estadual</label>
                <input value={ie} onChange={e => setIe(e.target.value)} className={inputClass} placeholder="IE (se contribuinte)" data-testid="qc-ie" />
              </div>
            </div>
          </>)}
        </div>
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-bold">Cancelar</button>
          <button onClick={submit} disabled={saving} className="px-5 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white rounded-xl font-bold text-sm flex items-center gap-2" data-testid="quick-client-save">
            {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Save size={14} />}
            {isEdit ? 'Salvar Alteracoes' : 'Salvar e Selecionar'}
          </button>
        </div>
      </div>
    </div>
  );
};


/* ========================= TaxSummaryRow — iter156co =========================
   Linha compacta abaixo do Total NF exibindo os tributos aplicados
   (Natureza de Operacao + overrides por item). Se todos zerados, nao renderiza.
   Botao "▶" expande detalhes. */
const TaxSummaryRow = ({ items = [], tipo, natop, companyId, previewPayload = null }) => {
  const [expanded, setExpanded] = useState(false);
  // iter156ei: "CALCULO DO IMPOSTO" real (mesma rotina da emissao) — carregado ao expandir.
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${ERP}/company-settings?company_id=${companyId}`, { withCredentials: true })
      .then(({ data }) => setEnabled(data?.nfe_tax_preview !== false)).catch(() => {});
  }, [companyId]);
  const payloadKey = JSON.stringify(previewPayload || {});
  useEffect(() => {
    if (!expanded || !previewPayload || !(previewPayload.items || []).length) return;
    let alive = true;
    setPreviewLoading(true); setPreviewErr('');
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${ERP}/invoices/preview-taxes`, previewPayload, { withCredentials: true });
        if (alive) setPreview(data);
      } catch (err) {
        if (alive) { setPreview(null); setPreviewErr(err.response?.data?.detail || 'Nao foi possivel calcular'); }
      } finally { if (alive) setPreviewLoading(false); }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, payloadKey]);
  const natIcms = (natop && natop.icms) || {};
  const natPis = (natop && natop.pis) || {};
  const natCofins = (natop && natop.cofins) || {};
  const natIpi = (natop && natop.ipi) || {};
  const totals = React.useMemo(() => {
    let vBaseSt = 0, vIcmsSt = 0, vPis = 0, vCofins = 0, vIpi = 0, vIcms = 0;
    for (const it of items) {
      const lineBruto = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
      // Item override > natureza default
      const aliqSt = Number(it.icms_aliquota_st ?? natIcms.aliquota_st) || 0;
      const mva = Number(it.icms_mva ?? natIcms.mva) || 0;
      const redBc = Number(it.icms_red_bc ?? natIcms.red_bc) || 0;
      const aliqIcms = Number(it.icms_aliquota ?? natIcms.aliquota) || 0;
      const aliqPis = Number(it.pis_aliquota ?? natPis.aliquota) || 0;
      const aliqCofins = Number(it.cofins_aliquota ?? natCofins.aliquota) || 0;
      const aliqIpi = Number(it.ipi_aliquota ?? natIpi.aliquota) || 0;
      const cst = String(it.icms_cst || natIcms.cst || '');
      // ST
      if (aliqSt > 0) {
        const bcSt = lineBruto * (1 + mva / 100) * (1 - redBc / 100);
        vBaseSt += bcSt;
        vIcmsSt += Math.max(0, bcSt * aliqSt / 100 - lineBruto * 0.12);
      }
      // ICMS proprio (regime normal — CST 00/10/20/... nao Simples que comeca com 1)
      if (aliqIcms > 0 && !cst.startsWith('1') && cst !== '500') {
        vIcms += lineBruto * aliqIcms / 100;
      }
      vPis += lineBruto * aliqPis / 100;
      vCofins += lineBruto * aliqCofins / 100;
      vIpi += lineBruto * aliqIpi / 100;
    }
    return { vBaseSt, vIcmsSt, vPis, vCofins, vIpi, vIcms };
  }, [items, natIcms, natPis, natCofins, natIpi]);

  const hasAnyTax = totals.vIcmsSt > 0 || totals.vPis > 0 || totals.vCofins > 0 || totals.vIpi > 0 || totals.vIcms > 0;
  if (!hasAnyTax) return null;

  const chip = (label, value, color) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-${color}-50 text-${color}-700 border border-${color}-200`}>
      <span className="opacity-70 text-[9px] uppercase">{label}</span>
      <span>{fmtBRL(value)}</span>
    </span>
  );

  if (!enabled) return null;
  return (
    <div className="border border-slate-200 bg-slate-50/60 rounded-lg px-3 py-2" data-testid="invoice-tax-summary">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap flex-1">
          <span className="text-[10px] font-black uppercase text-slate-500 mr-1">Tributos aplicados:</span>
          {totals.vIcms > 0 && chip('ICMS', totals.vIcms, 'emerald')}
          {totals.vIcmsSt > 0 && chip('ICMS-ST', totals.vIcmsSt, 'teal')}
          {totals.vIpi > 0 && chip('IPI', totals.vIpi, 'amber')}
          {totals.vPis > 0 && chip('PIS', totals.vPis, 'sky')}
          {totals.vCofins > 0 && chip('COFINS', totals.vCofins, 'indigo')}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="text-slate-500 hover:text-violet-600 p-1 rounded transition-transform"
          data-testid="invoice-tax-summary-expand"
          title={expanded ? 'Recolher' : 'Expandir detalhes'}
        >
          <ChevronDown size={14} className={expanded ? 'rotate-180 transition-transform' : 'transition-transform'} />
        </button>
      </div>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-slate-200 space-y-2 text-[11px]" data-testid="invoice-tax-summary-details">
          {previewLoading && <div className="text-[10px] text-slate-400 animate-pulse">Calculando impostos (mesma rotina da emissao)...</div>}
          {previewErr && <div className="text-[10px] text-rose-600 font-bold" data-testid="tax-preview-error">{previewErr}</div>}
          {preview?.totals && (() => {
            const T = preview.totals;
            const cell = (label, val, tid) => (
              <div className="border border-slate-300 bg-white px-2 py-1" data-testid={tid}>
                <div className="text-[8px] font-black uppercase text-slate-500 tracking-wide">{label}</div>
                <div className="text-xs font-bold text-slate-800 text-right font-mono">{fmtBRL(val || 0)}</div>
              </div>
            );
            return (
              <div data-testid="tax-preview-danfe">
                <div className="text-[9px] font-black uppercase text-slate-600 mb-1">Calculo do imposto (como sai no DANFE)</div>
                <div className="grid grid-cols-5 gap-0.5">
                  {cell('Base calc. ICMS', T.vBC, 'tp-vbc')}{cell('Valor ICMS', T.vICMS, 'tp-vicms')}{cell('Base calc. ICMS ST', T.vBCST, 'tp-vbcst')}{cell('Valor ICMS ST', T.vST, 'tp-vst')}{cell('Valor produtos', T.vProd, 'tp-vprod')}
                  {cell('Valor frete', T.vFrete, 'tp-vfrete')}{cell('Valor seguro', T.vSeg, 'tp-vseg')}{cell('Desconto', T.vDesc, 'tp-vdesc')}{cell('Outras desp.', T.vOutro, 'tp-voutro')}{cell('Valor IPI', T.vIPI, 'tp-vipi')}
                  {cell('Valor PIS', T.vPIS, 'tp-vpis')}{cell('Valor COFINS', T.vCOFINS, 'tp-vcofins')}{cell('FCP ST', T.vFCPST, 'tp-vfcpst')}{cell('Trib. aprox.', T.vTotTrib, 'tp-vtottrib')}
                  <div className="border-2 border-violet-400 bg-violet-50 px-2 py-1" data-testid="tp-vnf">
                    <div className="text-[8px] font-black uppercase text-violet-700 tracking-wide">Valor total da nota</div>
                    <div className="text-sm font-black text-violet-800 text-right font-mono">{fmtBRL(T.vNF || 0)}</div>
                  </div>
                </div>
                {(preview.items || []).some(it => it.grupo) && (
                  <div className="mt-2 max-h-40 overflow-auto border border-slate-200 rounded">
                    <table className="w-full text-[10px]">
                      <thead className="bg-slate-100 text-slate-600 uppercase text-[8px] font-black sticky top-0">
                        <tr><th className="px-1.5 py-1 text-left">#</th><th className="px-1.5 py-1 text-left">Produto</th><th className="px-1.5 py-1">CFOP</th><th className="px-1.5 py-1">CST/CSOSN</th><th className="px-1.5 py-1 text-right">vProd</th><th className="px-1.5 py-1 text-right">BC ST</th><th className="px-1.5 py-1 text-right">ICMS ST</th><th className="px-1.5 py-1 text-right">pCredSN</th></tr>
                      </thead>
                      <tbody>
                        {preview.items.map(it => (
                          <tr key={it.nItem} className="border-t border-slate-100" data-testid={`tp-item-${it.nItem}`}>
                            <td className="px-1.5 py-0.5">{it.nItem}</td><td className="px-1.5 py-0.5 truncate max-w-[180px]">{it.xProd}</td><td className="px-1.5 py-0.5 text-center font-mono">{it.CFOP}</td>
                            <td className="px-1.5 py-0.5 text-center font-mono">{it.icms?.CSOSN || it.icms?.CST || '-'}</td>
                            <td className="px-1.5 py-0.5 text-right font-mono">{fmtBRL(it.vProd)}</td>
                            <td className="px-1.5 py-0.5 text-right font-mono">{it.icms?.vBCST ? fmtBRL(Number(it.icms.vBCST)) : '-'}</td>
                            <td className="px-1.5 py-0.5 text-right font-mono">{it.icms?.vICMSST ? fmtBRL(Number(it.icms.vICMSST)) : '-'}</td>
                            <td className="px-1.5 py-0.5 text-right font-mono">{it.icms?.pCredSN ? `${it.icms.pCredSN}%` : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="text-[10px] italic text-slate-400 pt-1">
            {preview ? 'Valores calculados pela MESMA rotina da emissao (natureza + overrides por item, deducao do ICMS proprio, ST embutido quando configurado). E o que sera transmitido a SEFAZ.' : 'Chips acima sao estimativa local; expanda para o calculo real.'}
          </div>
        </div>
      )}
    </div>
  );
};

/* ========================= FiscalQuickEditModal — iter77ak / iter156co ========================= */
const FiscalQuickEditModal = ({ item, onClose, onSave }) => {
  const [ncm, setNcm] = useState(item.ncm || '');
  const [cest, setCest] = useState(item.cest || '');
  const [cfop, setCfop] = useState(item.cfop || '');
  const [origem, setOrigem] = useState(item.origem || '0');
  const [unitCom, setUnitCom] = useState(item.unit_commercial || '');
  const [icmsCst, setIcmsCst] = useState(item.icms_cst || '');
  const [icmsAliquota, setIcmsAliquota] = useState(item.icms_aliquota ?? '');
  // iter156co: campos ST completos (aliquota ST, MVA, reducao BC).
  const [icmsAliqSt, setIcmsAliqSt] = useState(item.icms_aliquota_st ?? '');
  const [icmsMva, setIcmsMva] = useState(item.icms_mva ?? '');
  const [icmsRedBc, setIcmsRedBc] = useState(item.icms_red_bc ?? '');
  const [pisCst, setPisCst] = useState(item.pis_cst || '');
  const [pisAliquota, setPisAliquota] = useState(item.pis_aliquota ?? '');
  const [cofinsCst, setCofinsCst] = useState(item.cofins_cst || '');
  const [cofinsAliquota, setCofinsAliquota] = useState(item.cofins_aliquota ?? '');
  const [ipiCst, setIpiCst] = useState(item.ipi_cst || '');
  const [ipiAliquota, setIpiAliquota] = useState(item.ipi_aliquota ?? '');
  const [updateMaster, setUpdateMaster] = useState(false);

  // Preview do calculo (mesma formula do backend: sefaz.py::iter156cl)
  const preview = React.useMemo(() => {
    const lineBruto = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
    const aliqSt = Number(icmsAliqSt) || 0;
    const mva = Number(icmsMva) || 0;
    const redBc = Number(icmsRedBc) || 0;
    const aliqPis = Number(pisAliquota) || 0;
    const aliqCofins = Number(cofinsAliquota) || 0;
    const aliqIpi = Number(ipiAliquota) || 0;
    const baseSt = lineBruto * (1 + mva / 100) * (1 - redBc / 100);
    const icmsStTotal = baseSt * aliqSt / 100;
    // v_deducao 12%/4% do Convenio 142/18 — mostra a estimativa nacional (12%)
    const vDeducao = lineBruto * 0.12;
    const vIcmsSt = Math.max(0, icmsStTotal - vDeducao);
    return {
      lineBruto,
      baseSt: baseSt.toFixed(2),
      vIcmsSt: vIcmsSt.toFixed(2),
      vPis: (lineBruto * aliqPis / 100).toFixed(2),
      vCofins: (lineBruto * aliqCofins / 100).toFixed(2),
      vIpi: (lineBruto * aliqIpi / 100).toFixed(2),
    };
  }, [item.quantity, item.unit_price, icmsAliqSt, icmsMva, icmsRedBc, pisAliquota, cofinsAliquota, ipiAliquota]);

  const submit = () => {
    onSave({
      ncm: ncm.replace(/\D/g, ''),
      cest: cest.replace(/\D/g, ''),
      cfop: cfop.replace(/\D/g, ''),
      origem,
      unit_commercial: unitCom,
      icms_cst: icmsCst,
      icms_aliquota: icmsAliquota === '' ? null : Number(icmsAliquota),
      icms_aliquota_st: icmsAliqSt === '' ? null : Number(icmsAliqSt),
      icms_mva: icmsMva === '' ? null : Number(icmsMva),
      icms_red_bc: icmsRedBc === '' ? null : Number(icmsRedBc),
      pis_cst: pisCst,
      pis_aliquota: pisAliquota === '' ? null : Number(pisAliquota),
      cofins_cst: cofinsCst,
      cofins_aliquota: cofinsAliquota === '' ? null : Number(cofinsAliquota),
      ipi_cst: ipiCst,
      ipi_aliquota: ipiAliquota === '' ? null : Number(ipiAliquota),
    }, updateMaster);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="fiscal-edit-modal">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh] my-4">
        <div className="bg-gradient-to-r from-violet-600 to-fuchsia-500 px-5 py-4 text-white flex items-center justify-between sticky top-0 z-10">
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Tributação do Item</p>
            <p className="text-base font-black truncate max-w-md">{item.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="fiscal-edit-close"><X /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          {/* SECAO 1: Classificacao */}
          <section>
            <h4 className="text-[11px] font-black uppercase text-slate-500 mb-2 flex items-center gap-1.5"><FileText size={12} className="text-violet-500"/>Classificação Fiscal</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>NCM (8 dígitos)</label>
                <input value={ncm} onChange={e => setNcm(e.target.value.replace(/\D/g, '').slice(0, 8))} className={`${inputClass} font-mono`} maxLength={8} data-testid="fiscal-edit-ncm" />
              </div>
              <div>
                <label className={labelCls}>CEST (7 dígitos)</label>
                <input value={cest} onChange={e => setCest(e.target.value.replace(/\D/g, '').slice(0, 7))} className={`${inputClass} font-mono`} maxLength={7} data-testid="fiscal-edit-cest" />
              </div>
              <div>
                <label className={labelCls}>CFOP (override)</label>
                <input value={cfop} onChange={e => setCfop(e.target.value.replace(/\D/g, '').slice(0, 4))} className={`${inputClass} font-mono`} maxLength={4} placeholder="usar natureza" data-testid="fiscal-edit-cfop" />
              </div>
              <div>
                <label className={labelCls}>Origem</label>
                <select value={origem} onChange={e => setOrigem(e.target.value)} className={inputClass} data-testid="fiscal-edit-origem">
                  <option value="0">0 - Nacional</option>
                  <option value="1">1 - Estrangeira (Imp. Direta)</option>
                  <option value="2">2 - Estrangeira (Adq. Merc. Int.)</option>
                  <option value="3">3 - Nacional &gt; 40% conteudo importado</option>
                  <option value="4">4 - Nacional - Processos Basicos</option>
                  <option value="5">5 - Nacional &lt;= 40% conteudo importado</option>
                  <option value="6">6 - Estrangeira (Imp. Direta - sem similar)</option>
                  <option value="7">7 - Estrangeira (Adq. - sem similar)</option>
                  <option value="8">8 - Nacional &gt; 70% conteudo importado</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Unidade (uCom)</label>
                <input value={unitCom} onChange={e => setUnitCom(e.target.value.toUpperCase().slice(0, 6))} className={inputClass} placeholder="UN, KG, PC..." data-testid="fiscal-edit-unit" />
              </div>
            </div>
          </section>

          {/* SECAO 2: ICMS (proprio + ST) */}
          <section className="bg-emerald-50/40 border border-emerald-100 rounded-xl p-3">
            <h4 className="text-[11px] font-black uppercase text-emerald-700 mb-2 flex items-center gap-1.5">ICMS + Substituição Tributária</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>CST/CSOSN</label>
                <input value={icmsCst} onChange={e => setIcmsCst(e.target.value.replace(/\D/g, '').slice(0, 4))} className={`${inputClass} font-mono`} placeholder="102, 201, 500..." data-testid="fiscal-edit-icms-cst" />
              </div>
              <div>
                <label className={labelCls}>Alíquota ICMS (%)</label>
                <input type="number" step="0.01" value={icmsAliquota} onChange={e => setIcmsAliquota(e.target.value)} className={inputClass} data-testid="fiscal-edit-icms-aliq" />
              </div>
              <div>
                <label className={labelCls}>Alíquota ST (%)</label>
                <input type="number" step="0.01" value={icmsAliqSt} onChange={e => setIcmsAliqSt(e.target.value)} className={inputClass} placeholder="17" data-testid="fiscal-edit-icms-aliq-st" />
              </div>
              <div>
                <label className={labelCls}>MVA (%)</label>
                <input type="number" step="0.01" value={icmsMva} onChange={e => setIcmsMva(e.target.value)} className={inputClass} placeholder="70" data-testid="fiscal-edit-icms-mva" />
              </div>
              <div>
                <label className={labelCls}>Redução BC ST (%)</label>
                <input type="number" step="0.01" value={icmsRedBc} onChange={e => setIcmsRedBc(e.target.value)} className={inputClass} placeholder="0" data-testid="fiscal-edit-icms-red-bc" />
              </div>
              <div className="col-span-2 md:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-2 pt-2 border-t border-emerald-200">
                <div className="text-[11px]"><span className="text-slate-500">Base ST:</span> <span className="font-black text-emerald-800">R$ {preview.baseSt}</span></div>
                <div className="text-[11px]"><span className="text-slate-500">vICMSST (nacional):</span> <span className="font-black text-emerald-800">R$ {preview.vIcmsSt}</span></div>
                <div className="text-[10px] text-slate-400 italic">Cálculo Conv. 142/18 (dedução 12% nacional / 4% importado)</div>
              </div>
            </div>
          </section>

          {/* SECAO 3: PIS + COFINS */}
          <section className="bg-sky-50/40 border border-sky-100 rounded-xl p-3">
            <h4 className="text-[11px] font-black uppercase text-sky-700 mb-2">PIS + COFINS</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>PIS CST</label>
                <input value={pisCst} onChange={e => setPisCst(e.target.value.replace(/\D/g, '').slice(0, 2))} className={`${inputClass} font-mono`} placeholder="01, 49..." data-testid="fiscal-edit-pis-cst" />
              </div>
              <div>
                <label className={labelCls}>PIS Alíquota (%)</label>
                <input type="number" step="0.01" value={pisAliquota} onChange={e => setPisAliquota(e.target.value)} className={inputClass} placeholder="0.65 ou 1.65" data-testid="fiscal-edit-pis-aliq" />
              </div>
              <div>
                <label className={labelCls}>COFINS CST</label>
                <input value={cofinsCst} onChange={e => setCofinsCst(e.target.value.replace(/\D/g, '').slice(0, 2))} className={`${inputClass} font-mono`} placeholder="01, 49..." data-testid="fiscal-edit-cofins-cst" />
              </div>
              <div>
                <label className={labelCls}>COFINS Alíquota (%)</label>
                <input type="number" step="0.01" value={cofinsAliquota} onChange={e => setCofinsAliquota(e.target.value)} className={inputClass} placeholder="3.00 ou 7.60" data-testid="fiscal-edit-cofins-aliq" />
              </div>
              <div className="col-span-2 md:col-span-4 pt-2 border-t border-sky-200 flex gap-4 text-[11px]">
                <div><span className="text-slate-500">vPIS:</span> <span className="font-black text-sky-800">R$ {preview.vPis}</span></div>
                <div><span className="text-slate-500">vCOFINS:</span> <span className="font-black text-sky-800">R$ {preview.vCofins}</span></div>
              </div>
            </div>
          </section>

          {/* SECAO 4: IPI */}
          <section className="bg-amber-50/40 border border-amber-100 rounded-xl p-3">
            <h4 className="text-[11px] font-black uppercase text-amber-700 mb-2">IPI</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>IPI CST</label>
                <input value={ipiCst} onChange={e => setIpiCst(e.target.value.replace(/\D/g, '').slice(0, 2))} className={`${inputClass} font-mono`} placeholder="50, 53, 99..." data-testid="fiscal-edit-ipi-cst" />
              </div>
              <div>
                <label className={labelCls}>IPI Alíquota (%)</label>
                <input type="number" step="0.01" value={ipiAliquota} onChange={e => setIpiAliquota(e.target.value)} className={inputClass} placeholder="5.00" data-testid="fiscal-edit-ipi-aliq" />
              </div>
              <div className="pt-2 flex items-end text-[11px]"><span className="text-slate-500 mr-1">vIPI:</span> <span className="font-black text-amber-800">R$ {preview.vIpi}</span></div>
            </div>
          </section>

          {item.product_id && (
            <label className="flex items-start gap-2 p-3 bg-violet-50 border border-violet-200 rounded-xl cursor-pointer" data-testid="fiscal-edit-update-master-label">
              <input
                type="checkbox"
                checked={updateMaster}
                onChange={e => setUpdateMaster(e.target.checked)}
                className="w-4 h-4 accent-violet-600 mt-0.5"
                data-testid="fiscal-edit-update-master"
              />
              <div>
                <p className="text-xs font-bold text-violet-800">Atualizar cadastro do produto</p>
                <p className="text-[11px] text-violet-600">Salva também em /api/products — corrige o cadastro mestre para futuras emissões.</p>
              </div>
            </label>
          )}
        </div>
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex justify-end gap-2 sticky bottom-0">
          <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-bold" data-testid="fiscal-edit-cancel">Cancelar</button>
          <button type="button" onClick={submit} className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-bold text-sm flex items-center gap-2" data-testid="fiscal-edit-save">
            <Save size={14} /> Aplicar
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoicesPage;

/* ========================= InutilizacaoModal ========================= */
const InutilizacaoModal = ({ companyId, onClose, onSaved }) => {
  const [modelo, setModelo] = useState('nfe');
  const [serie, setSerie] = useState(1);
  const [numIni, setNumIni] = useState('');
  const [numFin, setNumFin] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/fiscal-events/inutilizacao?company_id=${companyId}`, { withCredentials: true });
        setHistory(data.data || []);
      } catch { /* ignore */ }
    })();
  }, [companyId]);

  const submit = async (e) => {
    e.preventDefault();
    if (justificativa.trim().length < 15) { toast.error('Justificativa: minimo 15 caracteres'); return; }
    if (Number(numFin) < Number(numIni)) { toast.error('Numero final deve ser >= inicial'); return; }
    setSaving(true);
    const tid = toast.loading('Enviando inutilizacao ao SEFAZ...');
    try {
      const { data } = await axios.post(`${ERP}/fiscal-events/inutilizacao`, {
        company_id: companyId,
        modelo,
        serie: Number(serie),
        num_ini: Number(numIni),
        num_fin: Number(numFin),
        justificativa: justificativa.trim(),
      }, { withCredentials: true, timeout: 60000 });
      toast.dismiss(tid);
      if (data.status === 'homologada') {
        toast.success(`Inutilizacao homologada (protocolo ${data.protocolo})`);
        onSaved();
      } else {
        toast.error(`SEFAZ ${data.cstat}: ${(data.xmotivo || '').slice(0, 150)}`, { duration: 10000 });
        // refresh history below
        const { data: list } = await axios.get(`${ERP}/fiscal-events/inutilizacao?company_id=${companyId}`, { withCredentials: true });
        setHistory(list.data || []);
      }
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'Erro na inutilizacao', { duration: 10000 });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="inutilizacao-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
        <div className="bg-gradient-to-r from-amber-500 to-rose-500 px-5 py-4 text-white flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Inutilizacao de Numeracao</p>
            <p className="text-xl font-black">SEFAZ — Faixa de Numeros nao Utilizados</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="inut-close"><X /></button>
        </div>
        <form onSubmit={submit} className="overflow-y-auto p-5 space-y-4 flex-1">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
            ⚠️ Use somente quando houve um <span className="font-bold">gap</span> de numeracao (ex: numeracao gerada localmente sem autorizacao SEFAZ). SEFAZ vai marcar esses numeros como inutilizados — ninguem mais podera reusa-los.
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>Modelo</label>
              <select value={modelo} onChange={e => setModelo(e.target.value)} className={inputClass} data-testid="inut-modelo">
                <option value="nfe">NF-e (55)</option>
                <option value="nfce">NFC-e (65)</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Serie</label>
              <input type="number" min="1" value={serie} onChange={e => setSerie(e.target.value)} className={inputClass} data-testid="inut-serie" />
            </div>
            <div>
              <label className={labelCls}>Numero Inicial</label>
              <input type="number" min="1" required value={numIni} onChange={e => setNumIni(e.target.value)} className={inputClass} data-testid="inut-num-ini" />
            </div>
            <div>
              <label className={labelCls}>Numero Final</label>
              <input type="number" min="1" required value={numFin} onChange={e => setNumFin(e.target.value)} className={inputClass} data-testid="inut-num-fin" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Justificativa <span className="text-rose-500">*</span></label>
            <textarea required minLength={15} maxLength={255} value={justificativa} onChange={e => setJustificativa(e.target.value)} rows={3} className={inputClass} placeholder="Numeracao inutilizada por falha tecnica..." data-testid="inut-just" />
            <p className="text-[10px] text-slate-400 mt-1">{justificativa.length}/255 chars (min 15)</p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-800 text-sm font-bold" data-testid="inut-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white rounded-xl font-bold text-sm flex items-center gap-2" data-testid="inut-submit">
              {saving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
              <Send size={14} /> Inutilizar
            </button>
          </div>

          {history.length > 0 && (
            <div className="border-t border-slate-200 pt-4 space-y-2">
              <p className="text-xs font-black text-slate-500 uppercase tracking-wider">Historico</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500"><tr>
                    <th className="text-left py-1">Modelo/Serie</th>
                    <th className="text-left py-1">Faixa</th>
                    <th className="text-left py-1">Status</th>
                    <th className="text-left py-1">Protocolo</th>
                    <th className="text-left py-1">Motivo</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {history.map(h => (
                      <tr key={h.id} data-testid={`inut-row-${h.id}`}>
                        <td className="py-1.5">{(h.modelo || '').toUpperCase()} s{h.serie}</td>
                        <td className="py-1.5 font-mono">{h.num_ini}-{h.num_fin}</td>
                        <td className="py-1.5">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${h.status === 'homologada' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{h.status}</span>
                        </td>
                        <td className="py-1.5 font-mono text-[10px]">{h.protocolo || '—'}</td>
                        <td className="py-1.5 text-slate-500 truncate max-w-[180px]" title={h.xmotivo}>{(h.xmotivo || '').slice(0, 50)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};


/* ========================= CCeModal — Carta de Correcao ========================= */
const CCeModal = ({ invoice, onClose, onSaved }) => {
  const [texto, setTexto] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);

  const loadHistory = useCallback(async () => {
    setLoadingHist(true);
    try {
      const { data } = await axios.get(`${ERP}/fiscal-events/cce/${invoice.id}`, { withCredentials: true });
      setHistory(data.data || []);
    } catch { setHistory([]); }
    finally { setLoadingHist(false); }
  }, [invoice.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // iter156eb: impressao da CC-e registrada (DACCE) + download do XML procEventoNFe
  const fetchCceBlob = async (seq, kind) => {
    const { data } = await axios.get(`${ERP}/fiscal-events/cce/${invoice.id}/${seq}/${kind}`, { responseType: 'blob', withCredentials: true });
    return window.URL.createObjectURL(data);
  };
  const openCcePdf = async (seq) => {
    const tid = toast.loading('Gerando PDF da CC-e...');
    try {
      const url = await fetchCceBlob(seq, 'pdf');
      toast.dismiss(tid);
      const w = window.open(url, '_blank');
      if (!w) { const a = document.createElement('a'); a.href = url; a.download = `CCe_${invoice.numero}_seq${seq}.pdf`; a.click(); }
    } catch (err) {
      toast.dismiss(tid);
      let msg = 'Erro ao gerar PDF da CC-e';
      try { msg = JSON.parse(await err.response?.data?.text())?.detail || msg; } catch { /* ignore */ }
      toast.error(msg, { duration: 8000 });
    }
  };
  const downloadCceXml = async (seq) => {
    try {
      const url = await fetchCceBlob(seq, 'xml');
      const a = document.createElement('a'); a.href = url; a.download = `CCe_${invoice.numero}_seq${seq}.xml`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao baixar XML da CC-e'); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (texto.trim().length < 15) { toast.error('Texto: minimo 15 caracteres'); return; }
    setSending(true);
    const tid = toast.loading('Assinando e enviando CC-e...');
    try {
      const { data } = await axios.post(`${ERP}/fiscal-events/cce/${invoice.id}`, {
        texto_correcao: texto.trim(),
      }, { withCredentials: true, timeout: 60000 });
      toast.dismiss(tid);
      if (data.status === 'registrada') {
        toast.success(`CC-e #${data.sequencia} registrada (cstat ${data.cstat})`);
        setTexto('');
        loadHistory();
        onSaved();
      } else {
        toast.error(`SEFAZ ${data.cstat}: ${(data.xmotivo || '').slice(0, 150)}`, { duration: 10000 });
        loadHistory();
      }
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'Erro ao emitir CC-e', { duration: 10000 });
    } finally { setSending(false); }
  };

  const registradas = history.filter(c => c.status === 'registrada').length;
  const podeMais = registradas < 20;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="cce-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 text-white flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Carta de Correcao</p>
            <p className="text-xl font-black">CC-e — NF-e #{invoice.numero}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="cce-close"><X /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4 flex-1">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 space-y-1">
            <p>📝 Use a CC-e para corrigir <span className="font-bold">erros pequenos</span> em uma NF-e ja autorizada (ate 20 CC-es por nota, dentro de 720h da emissao).</p>
            <p>⛔ <span className="font-bold">NAO pode</span> corrigir: valores (preco, ICMS, base de calculo), quantidade, NCM, emitente, destinatario, datas.</p>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className={labelCls}>Texto da correcao <span className="text-rose-500">*</span></label>
              <textarea
                required minLength={15} maxLength={1000}
                value={texto} onChange={e => setTexto(e.target.value)}
                rows={4} className={inputClass}
                placeholder="Ex: Onde se le 'Transportador XPTO' leia-se 'Transportador Brasil Ltda'..."
                disabled={!podeMais}
                data-testid="cce-texto"
              />
              <p className="text-[10px] text-slate-400 mt-1">{texto.length}/1000 chars (min 15)</p>
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] text-slate-500">
                {registradas} CC-e(s) registrada(s) / 20 max
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-800 text-sm font-bold" data-testid="cce-cancel">Fechar</button>
                <button type="submit" disabled={sending || !podeMais} className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white rounded-xl font-bold text-sm flex items-center gap-2" data-testid="cce-submit">
                  {sending && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                  <Send size={14} /> Emitir CC-e
                </button>
              </div>
            </div>
          </form>

          <div className="border-t border-slate-200 pt-3">
            <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Historico</p>
            {loadingHist ? (
              <div className="py-3 flex justify-center"><div className="animate-spin rounded-full h-5 w-5 border-2 border-amber-500 border-t-transparent" /></div>
            ) : history.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Nenhuma CC-e registrada</p>
            ) : (
              <div className="space-y-2">
                {history.map((c, idx) => (
                  <div key={idx} className={`border rounded-xl p-3 ${c.status === 'registrada' ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-testid={`cce-history-${idx}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold">CC-e #{c.sequencia}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.status === 'registrada' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{c.status} • cstat {c.cstat}</span>
                    </div>
                    <p className="text-xs text-slate-700 mt-1">{c.texto_correcao}</p>
                    <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                      <span>{c.sent_at ? new Date(c.sent_at).toLocaleString('pt-BR') : ''}</span>
                      {c.protocolo && <span className="font-mono">Prot {c.protocolo}</span>}
                    </div>
                    {c.status === 'registrada' && (
                      <div className="flex gap-2 mt-2">
                        <button type="button" onClick={() => openCcePdf(c.sequencia)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold"
                          data-testid={`cce-print-${idx}`}>
                          <Printer size={12} /> Imprimir CC-e
                        </button>
                        <button type="button" onClick={() => downloadCceXml(c.sequencia)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-[11px] font-bold"
                          data-testid={`cce-xml-${idx}`}>
                          <Download size={12} /> XML
                        </button>
                      </div>
                    )}
                    {c.xmotivo && c.status !== 'registrada' && <p className="text-[10px] text-rose-600 italic mt-1">{c.xmotivo}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
